import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/utils/supabase';
import { toast } from 'sonner';
import { Pencil, DollarSign, TrendingUp, Users, User, ChevronLeft, ChevronRight, ChevronDown, FileText, Calendar, Wallet, ArrowUpDown, ArrowUp, ArrowDown, Calculator, CheckCircle2, Clock, Building2, Download, Ban, Search, Mail, Send, Loader2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { createComponentLogger } from '@/utils/logger';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { buildLoPayoutLedger } from '@/utils/banker-recon/loPayoutLedger';
import {
  computeBankedRecon,
  getLoanRevenueReimbursementBasis,
  type BankedReconInput
} from '@/utils/banker-recon/bankedNetProfit';
import { isGovernmentFundingPassThrough } from '@/utils/banker-recon/wireReconciliation';
import { isBuydownPassThrough } from '@/utils/banker-recon/buydownPassThrough';
import {
  buildFrozenFhaUfmipBridge,
  type FhaUfmipCaptureStatus,
  type FhaUfmipFlowMode
} from '@/utils/banker-recon/fhaUfmip';
import { useAuth } from '@/contexts/useAuth';
import {
  validatePaymentAmount,
  hasInvalidBulkPaymentAmounts,
  computeDialogDefault,
  computeBaseComp,
  computeBalance,
  computeLoBalances,
  isPayoutOutstanding
} from '@/utils/compensation/paymentCalculations';
import { CompensationFilters, type CompensationFilterValues } from '../compensation/CompensationFilters';
import { CompensationRowActions } from '../compensation/CompensationRowActions';
import { CompensationDrawer } from '../compensation/CompensationDrawer';
import { getReconDocumentsByIds, type ReconDocument } from '@/services/bankerReconService';
import { compSet, resolveBrokerCommissionSplit } from '@/services/compensationService';
import { getCompensationPayouts } from '@/services/compensationPayoutService';
import { PayoutAccountingDetails } from './PayoutAccountingDetails';
import { ObSelectablePlansPanel } from '../compensation/ObSelectablePlansPanel';
import { useRecaptureWorkspace } from '@/hooks/useRecaptureWorkspace';
import { RecapturePanel } from '../compensation/RecapturePanel';
import { PendingRecaptureNotice, PayoutRecaptureNotice, RecaptureAccountSummary } from '../compensation/RecaptureNotice';
import { RECAPTURE_CHANGED_EVENT } from '@/services/recaptureService';
import { ReconDocumentPopover } from '../compensation/ReconDocumentPopover';
import { ReportDateRangePicker } from '@/components/reports/date-range/ReportDateRangePicker';
import { makePreset, type ReportPeriod } from '@/components/reports/date-range/reportPeriod';
import {
  filterRevenuePayoutsByDateRange,
  formatRevenuePeriodLabel,
  buildRevenueSummaryRows,
  buildRevenueDetailRows,
  buildChannelComparisonRows,
  buildMonthlyComparisonRows,
  exportRevenueWorkbook
} from '@/utils/compensation/revenueHelpers';
import {
  getPayoutPaymentBatches,
  recordPayoutPaymentBatch,
  sendPayoutPaymentConfirmation,
  voidPayoutPaymentBatch,
  type PayoutPaymentBatch
} from '@/services/payoutPaymentService';
import {
  downloadPayoutPaymentLedger,
  filterAndSortPaymentBatches,
  type PaymentBatchSortDirection,
  type PaymentBatchSortField,
  type PaymentBatchStatusFilter
} from '@/utils/compensation/payoutPaymentLedger';
import {
  buildPayoutPaymentEmailPreview,
  type PayoutPaymentEmailPreview
} from '@/utils/compensation/payoutPaymentEmail';

const log = createComponentLogger('UserCompensation', 'compensation');

interface UserCompensation {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
  maximum_compensation: number | null;
  banked_compensation: number | null;
  broker_commission_split: number | null;
  banked_override_bps: number | null;
  broker_mentor_commission_split: number | null;
  override_recipient_id: string | null;
  mentor_id: string | null;
  mentorship_active: boolean;
  brokered_deals_completed: number;
}

interface EditDialogData {
  userId: string;
  userName: string;
  maximum_compensation: number | null;
  banked_compensation: number | null;
  broker_commission_split: number | null;
  banked_override_bps: number | null;
  broker_mentor_commission_split: number | null;
  override_recipient_id: string | null;
  mentor_id: string | null;
}

interface BankedOverrideEntry {
  id?: string | null;
  recipientId?: string | null;
  recipientName?: string | null;
  bps?: number | string | null;
}

interface BankerReconLineItemForComp {
  id: string;
  category: string;
  line_type: string;
  description: string;
  amount: number;
  reimbursable: boolean;
  nets_to_warehouse_advance: boolean | null;
  warehouse_retained_amount: number | null;
}

interface BankerReconForComp {
  detailsLoaded?: boolean;
  actual_purchase_price: number | null;
  processing_fee: number;
  underwriting_fee: number;
  company_revenue: number | null;
  wire_from_warehouse: number | null;
  compensation_type: string | null;
  compensation_value: number | null;
  lo_payout: number | null;
  volume_bonus_amount?: number;
  gos_amount: number | null;
  net_profit: number | null;
  lender_credit_given: number;
  interest_income: number;
  servicing_interest_income: number;
  flexclose_credit: number;
  fha_ufmip_flow_mode: FhaUfmipFlowMode | null;
  fha_ufmip_premium_amount: number | null;
  fha_ufmip_financed_amount: number | null;
  fha_ufmip_remitted_date: string | null;
  fha_ufmip_remitted_by: string | null;
  fha_ufmip_remitted_amount: number | null;
  fha_ufmip_verification_code: string | null;
  fha_ufmip_capture_status: FhaUfmipCaptureStatus | null;
  fha_ufmip_source_signature: string | null;
  fha_ufmip_capture_details: Record<string, unknown> | null;
  fha_ufmip_snapshot_at: string | null;
  fha_ufmip_company_variance: number | null;
  overrides: BankedOverrideEntry[];
  override_bps: number;
  override_recipient_id: string | null;
  override_recipient: { first_name: string | null; last_name: string | null } | null;
  loan_snapshot: Record<string, unknown> | null;
  line_items: BankerReconLineItemForComp[];
}

interface DbPerson {
  id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface DbBankerReconLineItem {
  id?: string | null;
  category?: string | null;
  line_type?: string | null;
  description?: string | null;
  amount?: number | string | null;
  reimbursable?: boolean | null;
  nets_to_warehouse_advance?: boolean | null;
  warehouse_retained_amount?: number | string | null;
}

interface DbBankerRecon {
  investor_purchase_date?: string | null;
  actual_purchase_price?: number | string | null;
  processing_fee?: number | string | null;
  underwriting_fee?: number | string | null;
  company_revenue?: number | string | null;
  wire_from_warehouse?: number | string | null;
  compensation_type?: string | null;
  compensation_value?: number | string | null;
  lo_payout?: number | string | null;
  volume_bonus_amount?: number | string | null;
  gos_amount?: number | string | null;
  net_profit?: number | string | null;
  lender_credit_given?: number | string | null;
  interest_income?: number | string | null;
  servicing_interest_income?: number | string | null;
  flexclose_credit?: number | string | null;
  fha_ufmip_flow_mode?: FhaUfmipFlowMode | null;
  fha_ufmip_premium_amount?: number | string | null;
  fha_ufmip_financed_amount?: number | string | null;
  fha_ufmip_remitted_date?: string | null;
  fha_ufmip_remitted_by?: string | null;
  fha_ufmip_remitted_amount?: number | string | null;
  fha_ufmip_verification_code?: string | null;
  fha_ufmip_capture_status?: FhaUfmipCaptureStatus | null;
  fha_ufmip_source_signature?: string | null;
  fha_ufmip_capture_details?: Record<string, unknown> | null;
  fha_ufmip_snapshot_at?: string | null;
  fha_ufmip_company_variance?: number | string | null;
  overrides?: unknown;
  override_bps?: number | string | null;
  override_recipient_id?: string | null;
  override_recipient?: { first_name?: string | null; last_name?: string | null } | null;
  loan_snapshot?: Record<string, unknown> | null;
  line_items?: DbBankerReconLineItem[] | null;
}

interface PayoutRecord {
  id?: string | null;
  loan_number?: string | null;
  borrower_name?: string | null;
  loan_amount?: number | string | null;
  payout_type?: string | null;
  gross_amount?: number | string | null;
  e_and_o_deduction?: number | string | null;
  net_amount?: number | string | null;
  hit_compensation_cap?: boolean | null;
  splits_description?: string | null;
  fund_date?: string | null;
  status?: string | null;
  company_total_revenue?: number | string | null;
  company_commission_base?: number | string | null;
  lo_commission_base?: number | string | null;
  processing_fee?: number | string | null;
  broker_split_percent?: number | string | null;
  gross_check_received?: number | string | null;
  lo_reimbursements_total?: number | string | null;
  settlement_statement_path?: string | null;
  final_check_path?: string | null;
  combined_document_path?: string | null;
  banker_recon_id?: string | null;
  payment_amount?: number | string | null;
  recaptured_amount?: number | string | null;
  payment_check_number?: string | null;
  payment_date?: string | null;
  recipient?: DbPerson | null;
  source_officer?: DbPerson | null;
  banker_recon?: DbBankerRecon | null;
}

// Payout data structure for UI
export interface PayoutData {
  id: string;
  loanNumber: string;
  borrowerName: string;
  loName: string;
  loId: string;
  recipientId: string;
  type: 'Banked' | 'Brokered' | 'Override' | 'Mentor' | 'Volume Bonus';
  source: string;
  loanAmount: number;
  grossPayout: number;
  eAndO: number;
  splits: string | null;
  netPayout: number;
  fundDate: string;
  purchaseDate: string | null;
  revenueDate: string;
  date: string;
  month: string;
  status: 'pending' | 'approved' | 'paid';
  hitCap: boolean;
  // Company revenue fields
  companyRevenue: number;
  companyCommissionBase: number;
  loCommissionBase: number;
  processingFee: number;
  brokerSplitPercent: number;
  grossCheckReceived: number;
  loReimbursementsTotal: number;
  // Document paths (scalar fallback for brokered payouts)
  settlementStatementPath: string | null;
  finalCheckPath: string | null;
  combinedDocumentPath: string | null;
  // All linked recon documents (enriched post-fetch for banked/override payouts)
  bankerReconId: string | null;
  reconDocuments: ReconDocument[];
  // Payment tracking
  paymentAmount: number;
  recapturedAmount?: number;
  paymentCheckNumber: string | null;
  paymentDate: string | null;
  // Banker recon data (for banked loans)
  bankerRecon: BankerReconForComp | null;
}

function isBankedOverrideEntry(value: unknown): value is BankedOverrideEntry {
  return typeof value === 'object' && value !== null && 'bps' in value;
}

function normalizeBankedOverrides(value: unknown): BankedOverrideEntry[] {
  return Array.isArray(value) ? value.filter(isBankedOverrideEntry) : [];
}

function formatPersonName(person: DbPerson | null | undefined, fallback: string): string {
  if (!person) {
return fallback;
}
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || fallback;
}

// Shared payout-mapping function — single source of truth for transforming DB records to PayoutData
export function mapRecordToPayout(record: PayoutRecord): PayoutData {
  const isBanked = record.payout_type === 'banked' || record.payout_type === 'volume_bonus';
  const purchaseDateStr = record.banker_recon?.investor_purchase_date || null;
  const revenueDateStr = isBanked && purchaseDateStr ? purchaseDateStr : record.fund_date;
  const revenueDate = parseAsLocalDate(revenueDateStr);
  const monthName = revenueDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const typeMap: Record<string, PayoutData['type']> = {
    banked: 'Banked',
    brokered: 'Brokered',
    override: 'Override',
    mentor: 'Mentor',
    volume_bonus: 'Volume Bonus'
  };

  const recipientName = formatPersonName(record.recipient, 'Unknown');
  const sourceName = formatPersonName(record.source_officer, 'Direct');
  const bankerRecon = record.banker_recon;

  return {
    id: record.id || '',
    loanNumber: record.loan_number || '',
    borrowerName: record.borrower_name || '',
    loName: recipientName,
    loId: record.recipient?.id || '',
    recipientId: record.recipient?.id || '',
    type: typeMap[record.payout_type] || 'Banked',
    source: sourceName,
    loanAmount: Number(record.loan_amount),
    grossPayout: Number(record.gross_amount),
    eAndO: Number(record.e_and_o_deduction),
    splits: record.splits_description,
    netPayout: Number(record.net_amount),
    fundDate: record.fund_date || '',
    purchaseDate: purchaseDateStr,
    revenueDate: revenueDateStr || '',
    date: revenueDateStr || '',
    month: monthName,
    status: record.status as 'pending' | 'approved' | 'paid',
    hitCap: Boolean(record.hit_compensation_cap),
    companyRevenue: Number(record.company_total_revenue || 0),
    companyCommissionBase: Number(record.company_commission_base || 0),
    loCommissionBase: Number(record.lo_commission_base || 0),
    processingFee: Number(record.processing_fee || 0),
    brokerSplitPercent: Number(record.broker_split_percent || 0),
    grossCheckReceived: Number(record.gross_check_received || 0),
    loReimbursementsTotal: Number(record.lo_reimbursements_total || 0),
    settlementStatementPath: record.settlement_statement_path,
    finalCheckPath: record.final_check_path,
    combinedDocumentPath: record.combined_document_path,
    bankerReconId: record.banker_recon_id || null,
    reconDocuments: [],
    paymentAmount: Number(record.payment_amount || 0),
    recapturedAmount: Number(record.recaptured_amount || 0),
    paymentCheckNumber: record.payment_check_number,
    paymentDate: record.payment_date,
    bankerRecon: bankerRecon ? {
      detailsLoaded: Array.isArray(bankerRecon.line_items),
      actual_purchase_price: Number(bankerRecon.actual_purchase_price || 0),
      processing_fee: Number(bankerRecon.processing_fee || 0),
      underwriting_fee: Number(bankerRecon.underwriting_fee || 0),
      company_revenue: bankerRecon.company_revenue === null || bankerRecon.company_revenue === undefined ? null : Number(bankerRecon.company_revenue),
      wire_from_warehouse: Number(bankerRecon.wire_from_warehouse || 0),
      compensation_type: bankerRecon.compensation_type || null,
      compensation_value: Number(bankerRecon.compensation_value || 0),
      lo_payout: Number(bankerRecon.lo_payout || 0),
      volume_bonus_amount: Number(bankerRecon.volume_bonus_amount || 0),
      gos_amount: Number(bankerRecon.gos_amount || 0),
      net_profit: bankerRecon.net_profit === null || bankerRecon.net_profit === undefined ? null : Number(bankerRecon.net_profit),
      lender_credit_given: Number(bankerRecon.lender_credit_given || 0),
      interest_income: Number(bankerRecon.interest_income || 0),
      servicing_interest_income: Number(bankerRecon.servicing_interest_income || 0),
      flexclose_credit: Number(bankerRecon.flexclose_credit || 0),
      fha_ufmip_flow_mode: bankerRecon.fha_ufmip_flow_mode || null,
      fha_ufmip_premium_amount:
        bankerRecon.fha_ufmip_premium_amount === null
        || bankerRecon.fha_ufmip_premium_amount === undefined
          ? null
          : Number(bankerRecon.fha_ufmip_premium_amount),
      fha_ufmip_financed_amount:
        bankerRecon.fha_ufmip_financed_amount === null
        || bankerRecon.fha_ufmip_financed_amount === undefined
          ? null
          : Number(bankerRecon.fha_ufmip_financed_amount),
      fha_ufmip_remitted_date: bankerRecon.fha_ufmip_remitted_date || null,
      fha_ufmip_remitted_by: bankerRecon.fha_ufmip_remitted_by || null,
      fha_ufmip_remitted_amount:
        bankerRecon.fha_ufmip_remitted_amount === null
        || bankerRecon.fha_ufmip_remitted_amount === undefined
          ? null
          : Number(bankerRecon.fha_ufmip_remitted_amount),
      fha_ufmip_verification_code: bankerRecon.fha_ufmip_verification_code || null,
      fha_ufmip_capture_status: bankerRecon.fha_ufmip_capture_status || null,
      fha_ufmip_source_signature: bankerRecon.fha_ufmip_source_signature || null,
      fha_ufmip_capture_details: bankerRecon.fha_ufmip_capture_details || null,
      fha_ufmip_snapshot_at: bankerRecon.fha_ufmip_snapshot_at || null,
      fha_ufmip_company_variance:
        bankerRecon.fha_ufmip_company_variance === null
        || bankerRecon.fha_ufmip_company_variance === undefined
          ? null
          : Number(bankerRecon.fha_ufmip_company_variance),
      overrides: normalizeBankedOverrides(bankerRecon.overrides),
      override_bps: Number(bankerRecon.override_bps || 0),
      override_recipient_id: bankerRecon.override_recipient_id || null,
      override_recipient: bankerRecon.override_recipient
        ? {
          first_name: bankerRecon.override_recipient.first_name || null,
          last_name: bankerRecon.override_recipient.last_name || null
        }
        : null,
      loan_snapshot: bankerRecon.loan_snapshot || null,
      line_items: (bankerRecon.line_items || []).map(item => ({
        id: item.id || '',
        category: item.category || '',
        line_type: item.line_type || '',
        description: item.description || '',
        amount: Number(item.amount || 0),
        reimbursable: item.reimbursable || false,
        nets_to_warehouse_advance: item.nets_to_warehouse_advance ?? null,
        warehouse_retained_amount: item.warehouse_retained_amount === null || item.warehouse_retained_amount === undefined
          ? null
          : Number(item.warehouse_retained_amount)
      }))
    } : null
  };
}

const BANKED_COMPENSATION_OPTIONS = [
  { value: '0.75', label: '0.75%' },
  { value: '1.00', label: '1.00%' },
  { value: '1.25', label: '1.25%' },
  { value: '1.50', label: '1.50%' },
  { value: '1.75', label: '1.75%' },
  { value: '2.00', label: '2.00%' },
  { value: '2.25', label: '2.25%' },
  { value: '2.50', label: '2.50%' },
  { value: '2.75', label: '2.75%' },
  { value: '3.00', label: '3.00%' }
];

export function UserCompensation() {
  const { user: authUser } = useAuth();
  const { workspace: recaptureWorkspace, warnings: recaptureWarnings, error: recaptureError, refresh: refreshRecaptures } = useRecaptureWorkspace();
  const recaptureRecords = recaptureWorkspace?.records || [];
  const showRecapture = Boolean(recaptureWorkspace?.enabled || recaptureRecords.length);
  const [users, setUsers] = useState<UserCompensation[]>([]);
  const [potentialMentors, setPotentialMentors] = useState<UserCompensation[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState<EditDialogData | null>(null);
  const [saving, setSaving] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserCompensation | null>(null);

  // Filter and pagination state
  const [filters, setFilters] = useState<CompensationFilterValues>({
    searchTerm: '',
    status: 'all',
    tier: 'all',
    relationship: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Tab state for Users vs Summary vs Account vs Revenue
  const [activeTab, setActiveTab] = useState<'users' | 'summary' | 'account' | 'revenue' | 'payments' | 'balances' | 'ob_plans' | 'recapture'>('users');
  const [summaryGrouping, setSummaryGrouping] = useState<'all' | 'lo' | 'month'>('all');
  const [revenueGrouping, setRevenueGrouping] = useState<'all' | 'lo' | 'month'>('lo');
  const [revenuePeriod, setRevenuePeriod] = useState<ReportPeriod>(() => makePreset('ytd'));

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Edit payout dialog state
  const [editPayoutDialog, setEditPayoutDialog] = useState<{
    payout: PayoutData;
    newRecipientId: string;
  } | null>(null);
  const [savingPayout, setSavingPayout] = useState(false);

  // Payment recording dialog state
  const [paymentDialog, setPaymentDialog] = useState<{
    payout: PayoutData;
    checkNumber: string;
    paymentDate: string;
    paymentAmount: string;
    memo: string;
    idempotencyKey: string;
  } | null>(null);
  const [savingPayment, setSavingPayment] = useState(false);

  // Bulk payment state
  const [selectedForPayment, setSelectedForPayment] = useState<Set<string>>(new Set());
  const [bulkPaymentDialog, setBulkPaymentDialog] = useState<{
    payouts: (PayoutData & { enteredAmount: string })[];
    checkNumber: string;
    paymentDate: string;
    memo: string;
    idempotencyKey: string;
  } | null>(null);

  // Whole-check void state. Allocations are immutable and remain visible.
  const [voidPaymentDialog, setVoidPaymentDialog] = useState<{
    batch: PayoutPaymentBatch;
    reason: string;
  } | null>(null);

  // Optional post-save email. Saving a check never sends automatically.
  const [paymentEmailDialog, setPaymentEmailDialog] = useState<PayoutPaymentEmailPreview | null>(null);
  const paymentEmailTolerance = paymentEmailDialog?.allocations.some(allocation => allocation.isVolumeBonus) ? 0 : 0.01;
  const [sendingPaymentEmail, setSendingPaymentEmail] = useState(false);

  const [paymentBatches, setPaymentBatches] = useState<PayoutPaymentBatch[]>([]);
  const [paymentBatchesLoading, setPaymentBatchesLoading] = useState(true);
  const [expandedPaymentBatches, setExpandedPaymentBatches] = useState<Set<string>>(new Set());
  const [paymentHistorySearch, setPaymentHistorySearch] = useState('');
  const [paymentHistoryStatus, setPaymentHistoryStatus] = useState<PaymentBatchStatusFilter>('all');
  const [paymentHistorySort, setPaymentHistorySort] = useState<PaymentBatchSortField>('paymentDate');
  const [paymentHistorySortDirection, setPaymentHistorySortDirection] = useState<PaymentBatchSortDirection>('desc');

  // Payments tab filter state
  const [paymentsPayMonth, setPaymentsPayMonth] = useState<string>('all');
  const [paymentsPayPeriod, setPaymentsPayPeriod] = useState<'all' | '15th' | 'EOM'>('all');

  // Balances tab state (hoisted from IIFE to comply with Rules of Hooks)
  const [balanceFilter, setBalanceFilter] = useState<'all' | 'outstanding' | 'settled'>('all');
  const [balanceSearch, setBalanceSearch] = useState('');
  const [expandedBalanceLOs, setExpandedBalanceLOs] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Account tab state - pay period filters
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Generate month options for the last 12 months
  const monthOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, currentMonth - i, 1);
      options.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
    }
    return options;
  }, [currentMonth, currentYear]);

  const [selectedPayMonth, setSelectedPayMonth] = useState(monthOptions[0].value);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<'all' | '15th' | 'EOM'>('all');

  // Sorting state for Summary payouts table
  type SortField = 'loanNumber' | 'loName' | 'type' | 'source' | 'loanAmount' | 'grossPayout' | 'eAndO' | 'netPayout' | 'date';
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ field, children, align = 'left' }: { field: SortField; children: React.ReactNode; align?: 'left' | 'right' }) => (
    <TableHead
      className={`cursor-pointer hover:bg-muted/50 select-none ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );

  // Helper: Calculate pay date based on fund date
  // Rule: Loans funded 1st-14th pay at END OF MONTH of same month
  //       Loans funded 15th-end of month pay on 15th of FOLLOWING month
  const getPayDate = (fundDate: string): { payDate: string; payPeriod: '15th' | 'EOM'; payMonth: string } => {
    const date = parseAsLocalDate(fundDate);
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    if (day >= 1 && day <= 14) {
      // Funded 1-14: Pay at END OF MONTH of same month
      const lastDay = new Date(year, month + 1, 0).getDate();
      return {
        payDate: `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`,
        payPeriod: 'EOM',
        payMonth: `${year}-${String(month + 1).padStart(2, '0')}`
      };
    } else {
      // Funded 15-EOM: Pay on 15th of FOLLOWING month
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      return {
        payDate: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-15`,
        payPeriod: '15th',
        payMonth: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}`
      };
    }
  };

  // Helper: Calculate pay period for Payments tab (15th or End of Month)
  // Rule: Loans funded 1st-14th pay at END OF MONTH of same month
  //       Loans funded 15th-end of month pay on 15th of FOLLOWING month
  const getPaymentPeriod = (fundDate: string): { payDate: string; payPeriod: '15th' | 'EOM'; payMonth: string } => {
    const date = parseAsLocalDate(fundDate);
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();

    if (day >= 1 && day <= 14) {
      // Funded 1-14: Pay at END OF MONTH of same month
      const lastDay = new Date(year, month + 1, 0).getDate();
      return {
        payDate: `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`,
        payPeriod: 'EOM',
        payMonth: `${year}-${String(month + 1).padStart(2, '0')}`
      };
    } else {
      // Funded 15-EOM: Pay on 15th of FOLLOWING month
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      return {
        payDate: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-15`,
        payPeriod: '15th',
        payMonth: `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}`
      };
    }
  };

  // Enrich payout rows with all linked recon documents
  const enrichWithReconDocs = async (rows: PayoutData[]): Promise<PayoutData[]> => {
    const reconIds = [...new Set(rows.map(p => p.bankerReconId).filter((id): id is string => !!id))];
    if (reconIds.length === 0) {
return rows;
}
    try {
      const docMap = await getReconDocumentsByIds(reconIds);
      for (const payout of rows) {
        if (payout.bankerReconId && docMap[payout.bankerReconId]) {
          payout.reconDocuments = docMap[payout.bankerReconId];
        }
      }
    } catch (err) {
      log.warn('Recon document enrichment failed, falling back to scalar paths', { error: (err as Error).message });
    }
    return rows;
  };

  // Shared attachment cell renderer — delegates to ReconDocumentPopover
  const renderAttachmentCell = (payout: PayoutData) => (
    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
      <ReconDocumentPopover
        reconDocuments={payout.reconDocuments}
        settlementStatementPath={payout.settlementStatementPath}
        finalCheckPath={payout.finalCheckPath}
        payoutType={payout.type}
      />
    </TableCell>
  );

  // Payouts state - fetched from database
  const [payouts, setPayouts] = useState<PayoutData[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);
  const payoutsLoaded = useRef(false);
  const paymentBatchesLoaded = useRef(false);

  const loadPaymentBatches = useCallback(async (): Promise<PayoutPaymentBatch[]> => {
    try {
      if (!paymentBatchesLoaded.current) {
        setPaymentBatchesLoading(true);
      }
      const batches = await getPayoutPaymentBatches();
      setPaymentBatches(batches);
      paymentBatchesLoaded.current = true;
      return batches;
    } catch (error) {
      log.error('Failed to load payout payment ledger', {}, error as Error);
      toast.error('Failed to load payment history', {
        description: (error as Error).message
      });
      return [];
    } finally {
      setPaymentBatchesLoading(false);
    }
  }, []);

  // Fetch payouts from database
  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        if (!payoutsLoaded.current) {
          setPayoutsLoading(true);
        }

        const { data, error } = await getCompensationPayouts();

        if (error) {
          throw error;
        }

        // Banked loans require both fund_date AND investor_purchase_date (revenue recognized at purchase)
        // Brokered/other loans require fund_date only
        const transformedPayouts: PayoutData[] = ((data || []) as PayoutRecord[]).filter(r => {
          if (r.payout_type === 'banked') {
            return r.fund_date !== null && r.fund_date !== undefined && r.banker_recon?.investor_purchase_date !== null && r.banker_recon?.investor_purchase_date !== undefined;
          }
          return r.fund_date !== null && r.fund_date !== undefined;
        }).map(mapRecordToPayout);

        await enrichWithReconDocs(transformedPayouts);
        setPayouts(transformedPayouts);
        payoutsLoaded.current = true;
        log.info('Payouts loaded from database', { count: transformedPayouts.length });
      } catch (error) {
        log.error('Failed to load payouts', {}, error as Error);
        toast.error('Failed to load payouts', {
          description: (error as Error).message
        });
      } finally {
        setPayoutsLoading(false);
      }
    };

    const refresh = () => {
      void Promise.all([fetchPayouts(), loadPaymentBatches()]);
    };
    refresh();
    window.addEventListener(RECAPTURE_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener(RECAPTURE_CHANGED_EVENT, refresh);
    };
  }, [loadPaymentBatches]);

  // Payroll data with calculated pay dates for Account tab
  const payrollData = useMemo(() => {
    return payouts.map(payout => {
      const { payDate, payPeriod } = getPayDate(payout.fundDate);
      const payDateObj = parseAsLocalDate(payDate);
      return {
        ...payout,
        payDate,
        payPeriod,
        payMonth: `${payDateObj.getFullYear()}-${String(payDateObj.getMonth() + 1).padStart(2, '0')}`,
        payDateFormatted: payDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      };
    });
  }, [payouts]);

  // Filtered payroll data based on selected month and pay period
  const filteredPayrollData = useMemo(() => {
    return payrollData.filter(payout => {
      // Filter by pay month (skip if 'all')
      if (selectedPayMonth !== 'all' && payout.payMonth !== selectedPayMonth) {
return false;
}

      // Filter by pay period (skip if 'all')
      if (selectedPayPeriod !== 'all' && payout.payPeriod !== selectedPayPeriod) {
return false;
}

      return true;
    });
  }, [payrollData, selectedPayMonth, selectedPayPeriod]);

  // Group payroll by recipient for the Account tab
  const payrollByRecipient = useMemo(() => {
    const grouped = filteredPayrollData.reduce((acc, payout) => {
      if (!acc[payout.loName]) {
        acc[payout.loName] = {
          name: payout.loName,
          payouts: [],
          totalGross: 0,
          totalEandO: 0,
          totalNet: 0
        };
      }
      acc[payout.loName].payouts.push(payout);
      acc[payout.loName].totalGross += payout.grossPayout;
      acc[payout.loName].totalEandO += payout.eAndO;
      acc[payout.loName].totalNet += payout.netPayout;
      return acc;
    }, {} as Record<string, { name: string; payouts: typeof filteredPayrollData; totalGross: number; totalEandO: number; totalNet: number }>);

    return Object.values(grouped).sort((a, b) => b.totalNet - a.totalNet);
  }, [filteredPayrollData]);

  // Payroll summary stats
  const payrollStats = useMemo(() => {
    const totalToPay = filteredPayrollData.reduce((sum, p) => sum + p.netPayout, 0);
    const totalEandO = filteredPayrollData.reduce((sum, p) => sum + p.eAndO, 0);
    const recipientCount = new Set(filteredPayrollData.map(p => p.loName)).size;
    const loanCount = new Set(filteredPayrollData.map(p => p.loanNumber)).size;
    const pendingCount = filteredPayrollData.filter(p => p.status === 'pending').length;
    return { totalToPay, totalEandO, recipientCount, loanCount, pendingCount };
  }, [filteredPayrollData]);

  // Sort payouts helper
  const sortPayoutsData = useCallback((data: PayoutData[]) => {
    return [...data].sort((a, b) => {
      let aVal: string | number = a[sortField] ?? '';
      let bVal: string | number = b[sortField] ?? '';

      // Handle numeric fields
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string fields
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [sortField, sortDirection]);

  // Grouped payouts for Summary tab
  const groupedPayouts = useMemo(() => {
    if (summaryGrouping === 'all') {
      return { 'All Payouts': sortPayoutsData(payouts) };
    } else if (summaryGrouping === 'lo') {
      const grouped = payouts.reduce((acc, payout) => {
        const key = payout.loName;
        if (!acc[key]) {
acc[key] = [];
}
        acc[key].push(payout);
        return acc;
      }, {} as Record<string, PayoutData[]>);
      // Sort each group
      Object.keys(grouped).forEach(key => {
        grouped[key] = sortPayoutsData(grouped[key]);
      });
      return grouped;
    } else {
      const grouped = payouts.reduce((acc, payout) => {
        const key = payout.month;
        if (!acc[key]) {
acc[key] = [];
}
        acc[key].push(payout);
        return acc;
      }, {} as Record<string, PayoutData[]>);
      // Sort each group
      Object.keys(grouped).forEach(key => {
        grouped[key] = sortPayoutsData(grouped[key]);
      });
      return grouped;
    }
  }, [payouts, summaryGrouping, sortPayoutsData]);

  // Map a finalized payout record to BankedReconInput for the shared calculator
  const mapPayoutToBankedInput = useCallback((payout: PayoutData): BankedReconInput => {
    const recon = payout.bankerRecon!;
    const activeOverrides = recon.overrides.filter(o => Number(o.bps || 0) > 0);
    const normalizedLineItems = recon.line_items.map(li => ({
      category: li.category,
      line_type: li.line_type || li.id,
      description: li.description,
      amount: li.amount,
      reimbursable: li.reimbursable,
      nets_to_warehouse_advance: li.nets_to_warehouse_advance ?? null,
      warehouse_retained_amount: li.warehouse_retained_amount ?? null
    }));
    const loanType = typeof recon.loan_snapshot?.trans_details_loan_type === 'string'
      ? recon.loan_snapshot.trans_details_loan_type
      : null;
    const fhaUfmip = buildFrozenFhaUfmipBridge(
      recon as unknown as Record<string, unknown>,
      normalizedLineItems,
      loanType
    );
    return {
      loanAmount: payout.loanAmount || 0,
      actualPurchasePrice: recon.actual_purchase_price || 0,
      compensationType: recon.compensation_type || 'percentage',
      compensationValue: recon.compensation_value || 0,
      volumeBonusAmount: recon.volume_bonus_amount || 0,
      overrides: activeOverrides.length > 0
        ? activeOverrides.map(o => ({ bps: Number(o.bps) || 0 }))
        : (recon.override_bps ? [{ bps: Number(recon.override_bps) }] : []),
      processingFee: recon.processing_fee || 0,
      underwritingFee: recon.underwriting_fee || 0,
      interestIncome: recon.interest_income || 0,
      servicingInterestIncome: recon.servicing_interest_income || 0,
      flexcloseCredit: recon.flexclose_credit || 0,
      loanType,
      fhaUfmip,
      lineItems: normalizedLineItems
    };
  }, []);

  // Live net profit calculation for banked loans — uses shared calculator (single source of truth)
  const computeBankedNetProfit = useCallback((payout: PayoutData): number => {
    if (!payout.bankerRecon) {
return 0;
}
    if (Number.isFinite(payout.bankerRecon.net_profit)) {
      return Number(payout.bankerRecon.net_profit);
    }
    return computeBankedRecon(mapPayoutToBankedInput(payout)).netProfit;
  }, [mapPayoutToBankedInput]);

  // Summary stats calculated from payouts data - split by type
  const summaryStats = useMemo(() => {
    const brokeredOnly = payouts.filter(p => p.type === 'Brokered');
    const mentorPayouts = payouts.filter(p => p.type === 'Mentor');
    const overridePayouts = payouts.filter(p => p.type === 'Override');
    const volumeBonusPayouts = payouts.filter(p => p.type === 'Volume Bonus');
    const banked = payouts.filter(p => p.type === 'Banked');

    // Brokered metrics (excludes Mentor and Override)
    const brokeredGrossChecks = brokeredOnly.reduce((sum, p) => sum + (p.grossCheckReceived || 0), 0);
    const brokeredCommissions = brokeredOnly.reduce((sum, p) => sum + (p.netPayout || 0), 0);
    const brokeredCompanyRevenue = brokeredOnly.reduce((sum, p) => sum + (p.companyRevenue || 0), 0);
    const brokeredUnits = new Set(brokeredOnly.map(p => p.loanNumber)).size;
    const brokeredTotalLoanAmt = brokeredOnly.reduce((sum, p) => sum + (p.loanAmount || 0), 0);

    // Mentor metrics
    const mentorCommissions = mentorPayouts.reduce((sum, p) => sum + (p.netPayout || 0), 0);
    const mentorUnits = mentorPayouts.length;

    // Override metrics
    const overrideCommissions = overridePayouts.reduce((sum, p) => sum + (p.netPayout || 0), 0);
    const overrideUnits = overridePayouts.length;

    // Banked metrics
    const bankedCompanyRevenue = banked.reduce((sum, p) => sum + (p.companyRevenue || 0), 0);
    const bankedLoCommissions = banked.reduce((sum, p) => sum + (p.netPayout || 0), 0);
    const bankedNetProfit = banked.reduce((sum, p) => sum + computeBankedNetProfit(p), 0);
    const bankedUnits = new Set(banked.map(p => p.loanNumber)).size;
    const bankedTotalLoanAmt = banked.reduce((sum, p) => sum + (p.loanAmount || 0), 0);

    return {
      brokeredGrossChecks, brokeredCommissions, brokeredCompanyRevenue, brokeredUnits, brokeredTotalLoanAmt,
      mentorCommissions, mentorUnits,
      volumeBonusTotal: volumeBonusPayouts.reduce((sum, payout) => sum + payout.netPayout, 0),
      volumeBonusCount: volumeBonusPayouts.length,
      overrideCommissions, overrideUnits,
      bankedCompanyRevenue, bankedLoCommissions, bankedNetProfit, bankedUnits, bankedTotalLoanAmt
    };
  }, [payouts, computeBankedNetProfit]);

  // Mentor lookup: loan number → mentor name (for "Mentored by" display on brokered payouts)
  const mentorByLoan = useMemo(() => {
    const map = new Map<string, string>();
    payouts.filter(p => p.type === 'Mentor').forEach(p => {
      map.set(p.loanNumber, p.loName);
    });
    return map;
  }, [payouts]);

  // Revenue period filter (excludes Override/Mentor — they're shown in LO Payouts tab, not company revenue)
  const revenueFilteredPayouts = useMemo(() => {
    const revenueEligible = payouts.filter((p): p is PayoutData & { type: 'Banked' | 'Brokered' } => p.type === 'Banked' || p.type === 'Brokered');
    return filterRevenuePayoutsByDateRange(revenueEligible, { from: revenuePeriod.from, to: revenuePeriod.to });
  }, [payouts, revenuePeriod]);

  // Company Revenue stats
  const revenueStats = useMemo(() => {
    const brokered = revenueFilteredPayouts.filter(p => p.type === 'Brokered');
    const banked = revenueFilteredPayouts.filter(p => p.type === 'Banked');

    const brokeredRevenue = brokered.reduce((sum, p) => sum + p.companyRevenue, 0);
    const brokeredTotalLoanAmt = brokered.reduce((sum, p) => sum + p.loanAmount, 0);
    const brokeredUnits = new Set(brokered.map(p => p.loanNumber)).size;
    const brokeredAvgLoan = brokeredUnits > 0 ? brokeredTotalLoanAmt / brokeredUnits : 0;
    const brokeredAvgRevenue = brokeredUnits > 0 ? brokeredRevenue / brokeredUnits : 0;

    const bankedNetProfit = banked.reduce((sum, p) => sum + computeBankedNetProfit(p), 0);
    const bankedTotalLoanAmt = banked.reduce((sum, p) => sum + p.loanAmount, 0);
    const bankedUnits = new Set(banked.map(p => p.loanNumber)).size;
    const bankedAvgLoan = bankedUnits > 0 ? bankedTotalLoanAmt / bankedUnits : 0;
    const bankedAvgProfit = bankedUnits > 0 ? bankedNetProfit / bankedUnits : 0;

    return {
      brokeredRevenue, brokeredTotalLoanAmt, brokeredUnits, brokeredAvgLoan, brokeredAvgRevenue,
      bankedNetProfit, bankedTotalLoanAmt, bankedUnits, bankedAvgLoan, bankedAvgProfit
    };
  }, [revenueFilteredPayouts, computeBankedNetProfit]);

  // Dynamic period label for revenue tab and export
  const revenuePeriodLabel = useMemo(
    () => formatRevenuePeriodLabel(
      { from: revenuePeriod.from, to: revenuePeriod.to },
      revenuePeriod.mode === 'qtd' ? 'this_quarter'
        : revenuePeriod.mode === 'lastMonth' ? 'last_month'
        : revenuePeriod.mode === 'lastQuarter' ? 'last_quarter'
        : revenuePeriod.mode === 'lastYear' ? 'last_year'
        : revenuePeriod.mode
    ),
    [revenuePeriod]
  );

  // Grouped payouts for Revenue tab (grouped by LO by default)
  const groupedRevenuePayouts = useMemo(() => {
    if (revenueGrouping === 'all') {
      return { 'All Loans': sortPayoutsData(revenueFilteredPayouts) };
    } else if (revenueGrouping === 'lo') {
      const grouped = revenueFilteredPayouts.reduce((acc, payout) => {
        const key = payout.loName;
        if (!acc[key]) {
acc[key] = [];
}
        acc[key].push(payout);
        return acc;
      }, {} as Record<string, PayoutData[]>);
      Object.keys(grouped).forEach(key => {
        grouped[key] = sortPayoutsData(grouped[key]);
      });
      return grouped;
    } else {
      const grouped = revenueFilteredPayouts.reduce((acc, payout) => {
        const key = payout.month;
        if (!acc[key]) {
acc[key] = [];
}
        acc[key].push(payout);
        return acc;
      }, {} as Record<string, PayoutData[]>);
      Object.keys(grouped).forEach(key => {
        grouped[key] = sortPayoutsData(grouped[key]);
      });
      return grouped;
    }
  }, [revenueFilteredPayouts, revenueGrouping, sortPayoutsData]);

  // Helper for currency formatting in breakdowns
  const fmtCurrency = (val: number) => `$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtBps = (amount: number, loanAmount: number) => (amount / loanAmount * 100).toFixed(3);
  const signedLoEffect = (amount: number) => ({
    prefix: amount < 0 ? '+' : '-',
    className: amount < 0 ? 'text-green-600' : 'text-red-600'
  });

  // Calculation breakdown component for expanded rows
  const CalculationBreakdown = ({ payout, showPayrollDetail = false }: { payout: PayoutData; showPayrollDetail?: boolean }) => {
    if (payout.type !== 'Banked' || !payout.bankerRecon) {
      return renderCalculationBreakdown({ payout, showPayrollDetail });
    }
    return <PayoutAccountingDetails reconId={payout.bankerReconId} source={payout.bankerRecon}
      initial={payout.bankerRecon.detailsLoaded ? payout.bankerRecon : undefined}>
      {details => renderCalculationBreakdown({
        payout: details ? { ...payout, bankerRecon: { ...payout.bankerRecon!, ...details, detailsLoaded: true } } : payout,
        showPayrollDetail
      })}
    </PayoutAccountingDetails>;
  };

  const renderCalculationBreakdown = ({ payout, showPayrollDetail = false }: { payout: PayoutData; showPayrollDetail?: boolean }) => {
    if (payout.type === 'Volume Bonus') {
      return <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
        <p className="font-medium">{payout.splits || 'Volume Bonus'}</p>
        <p>Allocated: {fmtCurrency(payout.netPayout)} · Cash paid: {fmtCurrency(payout.paymentAmount)} · Remaining: {fmtCurrency(computeBalance(payout.netPayout, payout.paymentAmount))}</p>
        <p className="text-muted-foreground">Separate cash bonus. No additional E&amp;O or reduction to this loan&apos;s LO Profitability.</p>
      </div>;
    }
    // --- OVERRIDE PAYOUT — simple display ---
    if (payout.type === 'Override') {
      // Find matching override BPS: check overrides[] array first, fallback to legacy column
      const reconOverrides = payout.bankerRecon?.overrides || [];
      const matchedOverride = reconOverrides.find(o => o.recipientId === payout.recipientId);
      const overrideBps = matchedOverride?.bps || payout.bankerRecon?.override_bps || 0;
      const sourceName = payout.source || 'N/A';
      return (
        <div className="p-4 bg-orange-50/50 rounded-lg space-y-2">
          <div className="text-sm font-medium text-orange-700">Override Payout</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-gray-600">Source LO:</span>
            <span className="font-medium">{sourceName}</span>
          </div>
          {overrideBps > 0 && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-gray-600">Override Rate:</span>
              <span className="font-mono">{overrideBps} bps</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-gray-600">Override Amount:</span>
            <span className="font-mono font-bold text-orange-700">{fmtCurrency(payout.grossPayout)}</span>
          </div>
        </div>
      );
    }

    // --- BANKED LOAN BREAKDOWN (uses shared calculator — single source of truth) ---
    if (payout.type === 'Banked' && payout.bankerRecon) {
      const recon = payout.bankerRecon;
      const loanAmt = payout.loanAmount;
      const wireFromWarehouse = recon.wire_from_warehouse || 0;

      // Use shared calculator for all financial values
      const result = computeBankedRecon(mapPayoutToBankedInput(payout));

      const { loPayout, gosAmount, interestIncome,
        totalOverrideAmount: _totalOverrideAmount, lenderCredit, warehouseInterest, shortageFunded,
        investorCostItems, warehouseCostItems, nonReimbursableUserFeeItems, otherLenderCostItems,
        reimbursableItems, escrowPassThrough, investorInterestCost: _investorInterestCost, governmentFundingPassThrough, buydownPassThrough } = result;
      const companyRevenue = recon.company_revenue ?? result.companyRevenue;
      const netProfit = recon.net_profit ?? result.netProfit;
      const loanType = typeof recon.loan_snapshot?.trans_details_loan_type === 'string'
        ? recon.loan_snapshot.trans_details_loan_type
        : null;
      const isGovernmentFundingCost = (item: { lineType: string; description: string }) =>
        isGovernmentFundingPassThrough({
          category: 'investor_cost',
          line_type: item.lineType,
          description: item.description
        }, loanType);
      const displayedInvestorCostItems = investorCostItems.filter(c =>
        !c.lineType.startsWith('escrow_deposit') &&
        !isGovernmentFundingCost(c) &&
        !isBuydownPassThrough({
          category: 'investor_cost',
          line_type: c.lineType,
          description: c.description
        }) &&
        c.amount !== 0
      );

      // Compensation label
      const compLabel = recon.compensation_type === 'percentage'
        ? `Less LO Compensation (${recon.compensation_value}%)`
        : 'Less LO Compensation';
      const processingFee = result.processingFee;
      const uwFee = result.underwritingFee;
      const totalReimbursable = result.totalReimbursable;
      const fhaUfmip = result.fhaUfmip;

      return (
        <div className="p-4 bg-blue-50/50 rounded-lg space-y-2">
          <div className="text-sm font-medium text-blue-700">Banked Loan — Net Profit Breakdown</div>

          {/* Header */}
          <div className="grid grid-cols-3 gap-2 text-[10px] text-blue-500 uppercase tracking-wide border-b border-blue-200 pb-1">
            <span></span>
            <span className="text-right">Basis Pts</span>
            <span className="text-right">Amount</span>
          </div>

          {/* Actual Purchase Price */}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <span className="text-blue-600">Actual Purchase Price</span>
            <span className="text-right font-mono text-blue-600">
              {recon.actual_purchase_price ? (recon.actual_purchase_price - 100).toFixed(3) : '—'}
            </span>
            <span className="text-right font-mono text-blue-700">{fmtCurrency(result.actualPurchaseDollar)}</span>
          </div>

          {/* Less LO Commission */}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <span className="text-blue-600">{loPayout < 0 ? 'LO Compensation Deduction Offset' : compLabel}</span>
            <span className={`text-right font-mono ${signedLoEffect(loPayout).className}`}>
              {signedLoEffect(loPayout).prefix}{fmtBps(Math.abs(loPayout), loanAmt)}
            </span>
            <span className={`text-right font-mono ${signedLoEffect(loPayout).className}`}>
              {signedLoEffect(loPayout).prefix}{fmtCurrency(loPayout)}
            </span>
          </div>
          {/* Override lines — right after LO Commission (matches BankerReconSummary order) */}
          {result.volumeBonusAmount > 0 && <div className="grid grid-cols-3 gap-2 text-sm">
            <span>Less Volume Bonus</span>
            <span className="text-right font-mono text-destructive">-{fmtBps(result.volumeBonusAmount, loanAmt)}</span>
            <span className="text-right font-mono text-destructive">-{fmtCurrency(result.volumeBonusAmount)}</span>
          </div>}
          {(() => {
            const ovrList = recon.overrides.length > 0
              ? recon.overrides
              : (recon.override_bps && recon.override_bps > 0
                ? [{ id: 'legacy', recipientName: recon.override_recipient ? `${recon.override_recipient.first_name} ${recon.override_recipient.last_name}` : null, bps: recon.override_bps }]
                : []);
            return ovrList.map((ovr) => {
              const amt = loanAmt * Number(ovr.bps || 0) / 10000;
              if (amt <= 0) {
return null;
}
              return (
                <div key={ovr.id || ovr.recipientId} className="grid grid-cols-3 gap-2 text-sm">
                  <span className="text-orange-700">
                    Override{ovr.recipientName ? ` to ${ovr.recipientName}` : ''} ({ovr.bps} bps)
                  </span>
                  <span className="text-right font-mono text-red-600">-{fmtBps(amt, loanAmt)}</span>
                  <span className="text-right font-mono text-red-600">-{fmtCurrency(amt)}</span>
                </div>
              );
            });
          })()}

          {/* Payroll Detail — Accounting tab only */}
          {showPayrollDetail && (() => {
            const loDeductions = recon.line_items.filter(i => i.category === 'lo_deduction');

            const baseComp = recon.compensation_type === 'percentage'
              ? loanAmt * (recon.compensation_value || 0) / 100
              : (recon.compensation_value || 0);
            const ledger = buildLoPayoutLedger({
              loanAmount: loanAmt,
              baseCompensation: baseComp,
              deductions: loDeductions.map(d => ({ label: d.description, amount: d.amount })),
              reimbursables: reimbursableItems.map(r => ({ label: r.description, amount: r.reimbursementAmount ?? r.amount })),
              eAndODeduction: payout.eAndO
            });

            return (
              <div className="ml-4 mt-2 mb-2 bg-gray-50 rounded border-l-2 border-gray-300 p-3 space-y-1">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide font-medium mb-2">
                  Payroll Detail — does not change company net profit above
                </p>
                {ledger.lines.map((line, idx) => {
                  // Subtotal and total: bold with top border, sign-aware color
                  if (line.type === 'subtotal' || line.type === 'total') {
                    const isTotalNeg = line.amount < 0;
                    const totalColor = isTotalNeg ? 'text-red-700' : 'text-green-700';
                    const weight = line.type === 'total' ? 'font-bold' : 'font-semibold';
                    return (
                      <div key={idx} className={`grid grid-cols-3 gap-2 text-sm border-t border-gray-300 pt-1 mt-1`}>
                        <span className={`text-gray-800 ${weight}`}>{line.label}</span>
                        <span className={`text-right font-mono ${weight} ${totalColor}`}>
                          {isTotalNeg ? '−' : ''}{fmtCurrency(Math.abs(line.amount))}
                        </span>
                        <span className="text-right font-mono text-gray-400">{line.percent || ''}</span>
                      </div>
                    );
                  }

                  const isNeg = line.type === 'deduction' || line.type === 'eAndO' || (line.type === 'reimbursable' && line.amount < 0);
                  const color = line.type === 'base' ? 'text-gray-700' : isNeg ? 'text-red-600' : 'text-green-600';

                  return (
                    <div key={idx} className={`grid grid-cols-3 gap-2 text-sm ${color}`}>
                      <span>
                        {line.type === 'deduction' || line.type === 'eAndO' ? '−' : line.type === 'reimbursable' ? (line.amount >= 0 ? '+' : '−') : ''}
                        {' '}{line.label}
                      </span>
                      <span className="text-right font-mono">
                        {isNeg ? '−' : ''}{fmtCurrency(Math.abs(line.amount))}
                      </span>
                      <span className="text-right font-mono text-gray-400">{line.percent || ''}</span>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* GOS */}
          <div className="grid grid-cols-3 gap-2 text-sm border-t border-blue-200 pt-2">
            <span className="text-blue-700 font-medium">GOS (Gain on Sale)</span>
            <span className="text-right font-mono text-blue-700 font-medium">{fmtBps(gosAmount, loanAmt)}</span>
            <span className="text-right font-mono text-blue-700 font-medium">{fmtCurrency(gosAmount)}</span>
          </div>

          {/* Processing Fee */}
          {processingFee > 0 && (
            <div className="grid grid-cols-3 gap-2 text-sm">
              <span className="text-blue-600">Processing Fee</span>
              <span className="text-right font-mono text-green-600">+{fmtBps(processingFee, loanAmt)}</span>
              <span className="text-right font-mono text-green-600">+{fmtCurrency(processingFee)}</span>
            </div>
          )}

          {/* UW Fee */}
          {uwFee > 0 && (
            <div className="grid grid-cols-3 gap-2 text-sm">
              <span className="text-blue-600">Underwriting Fee</span>
              <span className="text-right font-mono text-green-600">+{fmtBps(uwFee, loanAmt)}</span>
              <span className="text-right font-mono text-green-600">+{fmtCurrency(uwFee)}</span>
            </div>
          )}

          {/* Individual non-reimbursable loan revenue items */}
          {nonReimbursableUserFeeItems.map(fee => (
            <div key={fee.lineType} className="grid grid-cols-3 gap-2 text-sm">
              <span className="text-blue-600">{fee.description}</span>
              <span className="text-right font-mono text-green-600">+{fmtBps(fee.amount, loanAmt)}</span>
              <span className="text-right font-mono text-green-600">+{fmtCurrency(fee.amount)}</span>
            </div>
          ))}

          {/* Company Revenue Total */}
          <div className="grid grid-cols-3 gap-2 text-sm border-t border-blue-300 pt-2">
            <span className="text-blue-800 font-semibold">Company Revenue</span>
            <span className="text-right font-mono text-blue-800 font-semibold">{fmtBps(companyRevenue, loanAmt)}</span>
            <span className="text-right font-mono text-blue-800 font-semibold">{fmtCurrency(companyRevenue)}</span>
          </div>

          {/* Interest & Costs Section */}
          <div className="border-t border-blue-200 pt-2 mt-2">
            {/* Interest Charged at Closing */}
            {interestIncome > 0 && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-blue-600">Interest Charged at Closing</span>
                <span className="text-right font-mono text-green-600">+{fmtBps(interestIncome, loanAmt)}</span>
                <span className="text-right font-mono text-green-600">+{fmtCurrency(interestIncome)}</span>
              </div>
            )}

            {/* Investor Costs/Credits group */}
            {(displayedInvestorCostItems.length > 0 || escrowPassThrough > 0 || governmentFundingPassThrough > 0 || buydownPassThrough > 0) && (
              <>
                <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                  <span className="text-gray-500 font-semibold uppercase tracking-wide">Investor</span>
                  <span></span><span></span>
                </div>
                {displayedInvestorCostItems.map(item => {
                  const isCredit = item.amount < 0;
                  const absAmount = Math.abs(item.amount);
                  return (
                    <div key={item.lineType} className="grid grid-cols-3 gap-2 text-sm pl-2">
                      <span className="text-blue-600">
                        {isCredit ? `${item.description} Credit` : `Less ${item.description}`}
                      </span>
                      <span className={`text-right font-mono ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                        {isCredit ? '+' : '-'}{fmtBps(absAmount, loanAmt)}
                      </span>
                      <span className={`text-right font-mono ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                        {isCredit ? '+' : '-'}{fmtCurrency(absAmount)}
                      </span>
                    </div>
                  );
                })}
                {/* Escrow pass-through info */}
                {escrowPassThrough > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                    <span className="text-blue-400 italic text-xs">Escrow Pass-Through (wash)</span>
                    <span></span>
                    <span className="text-right font-mono text-gray-400 text-xs">{fmtCurrency(escrowPassThrough)}</span>
                  </div>
                )}
                {governmentFundingPassThrough > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                    <span className="text-blue-400 italic text-xs">Government Funding Pass-Through</span>
                    <span></span>
                    <span className="text-right font-mono text-gray-400 text-xs">{fmtCurrency(governmentFundingPassThrough)}</span>
                  </div>
                )}
                {buydownPassThrough > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                    <span className="text-blue-400 italic text-xs">Temporary Subsidy Buydown Pass-Through</span>
                    <span></span>
                    <span className="text-right font-mono text-gray-400 text-xs">{fmtCurrency(buydownPassThrough)}</span>
                  </div>
                )}
              </>
            )}

            {/* Warehouse Costs group */}
            {warehouseCostItems.some(c => c.amount !== 0) && (
              <>
                <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                  <span className="text-gray-500 font-semibold uppercase tracking-wide">Warehouse</span>
                  <span></span><span></span>
                </div>
                {warehouseInterest > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                    <span className="text-blue-600">Less Warehouse Interest</span>
                    <span className="text-right font-mono text-red-600">-{fmtBps(warehouseInterest, loanAmt)}</span>
                    <span className="text-right font-mono text-red-600">-{fmtCurrency(warehouseInterest)}</span>
                  </div>
                )}
                {warehouseCostItems
                  .filter(c => c.lineType !== 'warehouse_interest' && c.lineType !== 'shortage_funded' && c.amount !== 0)
                  .map(item => (
                    <div key={item.lineType} className="grid grid-cols-3 gap-2 text-sm pl-2">
                      <span className="text-blue-600">Less {item.description}</span>
                      <span className="text-right font-mono text-red-600">-{fmtBps(item.amount, loanAmt)}</span>
                      <span className="text-right font-mono text-red-600">-{fmtCurrency(item.amount)}</span>
                    </div>
                  ))}
                {/* Shortage funded pass-through info */}
                {shortageFunded > 0 && (
                  <div className="grid grid-cols-3 gap-2 text-sm pl-2">
                    <span className="text-blue-400 italic text-xs">Shortage Funded (wash)</span>
                    <span></span>
                    <span className="text-right font-mono text-gray-400 text-xs">{fmtCurrency(shortageFunded)}</span>
                  </div>
                )}
              </>
            )}

            {/* FlexClose Credit */}
            {recon.flexclose_credit > 0 && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-blue-600">FlexClose Credit</span>
                <span className="text-right font-mono text-green-600">+{fmtBps(recon.flexclose_credit, loanAmt)}</span>
                <span className="text-right font-mono text-green-600">+{fmtCurrency(recon.flexclose_credit)}</span>
              </div>
            )}

            {/* Lender Credit */}
            {lenderCredit > 0 && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-blue-600">Less Lender Credit Given</span>
                <span className="text-right font-mono text-red-600">-{fmtBps(lenderCredit, loanAmt)}</span>
                <span className="text-right font-mono text-red-600">-{fmtCurrency(lenderCredit)}</span>
              </div>
            )}

            {/* Other company expenses (warehouse-withheld MI premium, manual adjustment expenses) */}
            {otherLenderCostItems.filter(item => item.amount !== 0).map((item, index) => (
              <div key={`${item.lineType}-${index}`} className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-blue-600">Less {item.description}</span>
                <span className="text-right font-mono text-red-600">-{fmtBps(item.amount, loanAmt)}</span>
                <span className="text-right font-mono text-red-600">-{fmtCurrency(item.amount)}</span>
              </div>
            ))}

            {/* Servicing Interest Income */}
            {recon.servicing_interest_income > 0 && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-blue-600">Servicing Interest Income</span>
                <span className="text-right font-mono text-green-600">+{fmtBps(recon.servicing_interest_income, loanAmt)}</span>
                <span className="text-right font-mono text-green-600">+{fmtCurrency(recon.servicing_interest_income)}</span>
              </div>
            )}

            {fhaUfmip?.flowMode === 'lender_remitted' && (
              <div className="mt-2 space-y-1 rounded border border-teal-200 bg-teal-50/70 p-2">
                <div className="grid grid-cols-3 gap-2 text-xs font-semibold text-teal-800">
                  <span>FHA UFMIP Clearing</span>
                  <span></span>
                  <span className="text-right">Memo</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-teal-700">
                  <span>Premium retained at closing</span>
                  <span></span>
                  <span className="text-right font-mono">{fmtCurrency(fhaUfmip.clearingPremiumAmount)}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-teal-700">
                  <span>HUD remittance</span>
                  <span></span>
                  <span className="text-right font-mono">−{fmtCurrency(fhaUfmip.outsideRemittanceAmount)}</span>
                </div>
                {result.fhaUfmipCompanyVariance > 0 && (
                  <div className="grid grid-cols-3 gap-2 border-t border-teal-200 pt-1 text-xs">
                    <span className="font-medium text-blue-700">Less company-paid UFMIP variance</span>
                    <span className="text-right font-mono text-red-600">
                      -
                      {fmtBps(Math.abs(result.fhaUfmipCompanyVariance), loanAmt)}
                    </span>
                    <span className="text-right font-mono text-red-600">
                      -
                      {fmtCurrency(Math.abs(result.fhaUfmipCompanyVariance))}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Net Profit Total */}
          <div className="grid grid-cols-3 gap-2 text-sm border-t border-blue-400 pt-2 bg-blue-100 -mx-2 px-2 py-2 rounded">
            <span className="text-blue-900 font-bold">Net Profit</span>
            <span className="text-right font-mono text-blue-900 font-bold">{fmtBps(netProfit, loanAmt)}</span>
            <span className="text-right font-mono text-blue-900 font-bold">{fmtCurrency(netProfit)}</span>
          </div>

          {/* LO Reimbursements (fees marked reimbursable to LO) */}
          {reimbursableItems.length > 0 && (
            <div className="border-t border-green-300 pt-2 mt-2">
              <div className="text-xs text-green-700 uppercase tracking-wide mb-1 font-semibold">LO Reimbursements</div>
              {reimbursableItems.map(fee => {
                const reimbursementAmount = fee.reimbursementAmount ?? fee.amount;
                const isNeg = reimbursementAmount < 0;
                const feeColor = isNeg ? 'text-red-600' : 'text-green-600';
                const feePrefix = isNeg ? '−' : '+';
                return (
                  <div key={fee.lineType} className="grid grid-cols-3 gap-2 text-sm">
                    <span className={isNeg ? 'text-red-700' : 'text-green-700'}>{fee.description}</span>
                    <span className={`text-right font-mono ${feeColor}`}>{feePrefix}{fmtBps(Math.abs(reimbursementAmount), loanAmt)}</span>
                    <span className={`text-right font-mono ${feeColor}`}>{feePrefix}{fmtCurrency(Math.abs(reimbursementAmount))}</span>
                  </div>
                );
              })}
              {reimbursableItems.length > 1 && (
                <div className="grid grid-cols-3 gap-2 text-sm font-medium border-t border-green-200 pt-1 mt-1">
                  <span className={totalReimbursable < 0 ? 'text-red-800' : 'text-green-800'}>Total Reimbursements</span>
                  <span></span>
                  <span className={`text-right font-mono ${totalReimbursable < 0 ? 'text-red-800' : 'text-green-800'}`}>
                    {totalReimbursable < 0 ? '−' : ''}{fmtCurrency(Math.abs(totalReimbursable))}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Wire from Warehouse */}
          {wireFromWarehouse > 0 && (
            <div className="flex justify-between items-center pt-3 border-t border-blue-200 mt-2">
              <span className="text-sm font-medium text-blue-700">Wire from Warehouse</span>
              <span className="text-lg font-bold text-blue-800">{fmtCurrency(wireFromWarehouse)}</span>
            </div>
          )}

        </div>
      );
    }

    // --- MENTOR PAYOUT BREAKDOWN (authoritative stored values only) ---
    if (payout.type === 'Mentor') {
      return (
        <div className="p-4 bg-purple-50/50 rounded-lg space-y-2">
          <div className="text-sm font-medium text-purple-700">Mentor Commission Breakdown</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Source Loan Officer</span>
              <span className="font-medium">{payout.source}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Loan Amount</span>
              <span className="font-medium">{fmtCurrency(payout.loanAmount)}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mentor Split ({payout.brokerSplitPercent}%)</span>
              <span className="font-medium text-purple-600">{fmtCurrency(payout.loCommissionBase)}</span>
            </div>
            {payout.loReimbursementsTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plus: Reimbursements</span>
                <span className="text-green-600">+{fmtCurrency(payout.loReimbursementsTotal)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium">
              <span>Gross Mentor Commission</span>
              <span>{fmtCurrency(payout.grossPayout)}</span>
            </div>
            {payout.eAndO > 0 && (
              <>
                <Separator />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Less: E&O Insurance</span>
                  <span className="text-red-600">-{fmtCurrency(payout.eAndO)}</span>
                </div>
                <div className="flex justify-between font-medium text-purple-700">
                  <span>Net Mentor Payout</span>
                  <span>{fmtCurrency(payout.netPayout)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // --- BROKERED LOAN BREAKDOWN (existing split model) ---
    const loSplitPercent = payout.brokerSplitPercent;
    const companySplitPercent = 100 - loSplitPercent;
    const loReimbursements = payout.loReimbursementsTotal;

    // Detect mentor split from splits_description
    const hasMentorSplit = payout.splits?.toLowerCase().includes('mentor') ?? false;

    // Full E&O is always $50/loan (business constant)
    const FULL_EO = 50;

    // When mentor split is active, reconstruct the full net-for-split from authoritative grossCheckReceived
    // loReimbursementsTotal on the LO payout stores only LO reimbursements — exact when no mentor reimbursements
    let netForSplit: number;
    let fullLoCommission: number;
    let mentorDeduction = 0;
    let mentorSplitLabel = '';

    // Track whether reconstruction is reliable (false when mentor reimbursements cause drift)
    let reconstructionValid = true;

    if (hasMentorSplit && payout.grossCheckReceived > 0) {
      netForSplit = payout.grossCheckReceived - payout.processingFee - loReimbursements;
      fullLoCommission = Math.round(netForSplit * (loSplitPercent / 100) * 100) / 100;
      mentorDeduction = Math.round((fullLoCommission - payout.loCommissionBase) * 100) / 100;
      const mentorMatch = payout.splits?.match(/less\s+(\d+(?:\.\d+)?)\s*%\s*mentor/i);
      mentorSplitLabel = mentorMatch ? `${mentorMatch[1]}%` : '';

      // Defensive validation: if reconstruction doesn't reconcile (mentor reimbursements exist),
      // skip intermediate split lines to avoid showing incorrect math
      const expectedCompanyBase = Math.round(netForSplit * (companySplitPercent / 100) * 100) / 100;
      const reconstructionDrift = Math.abs(expectedCompanyBase - payout.companyCommissionBase);
      reconstructionValid = reconstructionDrift <= 1; // $1 tolerance for rounding
    } else {
      // Standard brokered path — loCommissionBase + companyCommissionBase = netForSplit
      netForSplit = payout.loCommissionBase + payout.companyCommissionBase;
      fullLoCommission = payout.loCommissionBase;
    }

    return (
      <div className="p-4 bg-muted/30 rounded-lg space-y-4">
        <div className="text-sm font-medium text-muted-foreground">Commission Breakdown</div>

        {/* Row 1: Gross Check */}
        <div className="flex justify-between items-center py-2 border-b">
          <span className="font-medium">Gross Check Received</span>
          <span className="text-lg font-bold">{fmtCurrency(payout.grossCheckReceived)}</span>
        </div>

        {/* Row 2: Deductions from Gross Check */}
        <div className="space-y-1 py-2 border-b">
          {loReimbursements > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Less: LO Reimbursements (direct to LO)</span>
              <span className="text-red-600">-{fmtCurrency(loReimbursements)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Less: Processing Fee</span>
            <span className="text-red-600">-{fmtCurrency(payout.processingFee)}</span>
          </div>
          <div className="flex justify-between pt-2 font-medium">
            <span>Net Available for Split</span>
            <span>{fmtCurrency(netForSplit)}</span>
          </div>
        </div>

        {/* Row 3: Split Breakdown */}
        <div className="grid grid-cols-2 gap-6 py-2 border-b">
          <div className="space-y-1">
            <div className="text-sm font-medium text-blue-600 mb-2">Loan Officer ({loSplitPercent}%)</div>
            {reconstructionValid ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{loSplitPercent}% of Net:</span>
                  <span>{fmtCurrency(fullLoCommission)}</span>
                </div>
                {hasMentorSplit && mentorDeduction > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground text-purple-600">Less: Mentor Split{mentorSplitLabel ? ` (${mentorSplitLabel})` : ''}:</span>
                    <span className="text-purple-600">-{fmtCurrency(mentorDeduction)}</span>
                  </div>
                )}
                {hasMentorSplit && (
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-muted-foreground">LO After Mentor:</span>
                    <span>{fmtCurrency(payout.loCommissionBase)}</span>
                  </div>
                )}
                {loReimbursements > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Plus: Reimbursements:</span>
                    <span className="text-green-600">+{fmtCurrency(loReimbursements)}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="flex justify-between text-sm font-medium">
                <span className="text-muted-foreground">LO Commission (after mentor):</span>
                <span>{fmtCurrency(payout.loCommissionBase)}</span>
              </div>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-green-600 mb-2">Company ({companySplitPercent}%)</div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{companySplitPercent}% of Net:</span>
              <span>{fmtCurrency(payout.companyCommissionBase)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Plus: Processing Fee:</span>
              <span className="text-green-600">+{fmtCurrency(payout.processingFee)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Plus: E&O Insurance:</span>
              <span className="text-green-600">+{fmtCurrency(hasMentorSplit ? FULL_EO : payout.eAndO)}</span>
            </div>
          </div>
        </div>

        {/* Row 4: Final Totals */}
        <div className="grid grid-cols-2 gap-6 pt-2">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-blue-800">LO Gross Payout</span>
              <span className="text-lg font-bold text-blue-600">{fmtCurrency(payout.grossPayout)}</span>
            </div>
            {payout.eAndO > 0 && (
              <>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="text-red-600">Less: E&O</span>
                  <span className="text-red-600">-{fmtCurrency(payout.eAndO)}</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="font-medium text-blue-800">LO Net Payout</span>
                  <span className="text-lg font-bold text-blue-600">{fmtCurrency(payout.netPayout)}</span>
                </div>
              </>
            )}
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex justify-between items-center">
              <span className="font-medium text-green-800">Company Revenue</span>
              <span className="text-lg font-bold text-green-600">{fmtCurrency(payout.companyRevenue)}</span>
            </div>
          </div>
        </div>

        {/* Cap Applied badge (only if applicable) */}
        {payout.hitCap && (
          <div className="flex flex-wrap gap-2 pt-2">
            <Badge variant="outline" className="text-blue-600 border-blue-300">Cap Applied</Badge>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('is_loan_officer', true)
        .order('last_name', { ascending: true });

      if (error) {
throw error;
}

      setUsers(data || []);
      // All users are loan officers, so they can all be potential mentors/override recipients
      setPotentialMentors(data || []);

      log.info('Loan officers loaded successfully', { count: data?.length || 0 });
    } catch (error) {
      log.error('Failed to load loan officers', {}, error as Error);
      toast.error('Failed to load loan officers', {
        description: (error as Error).message
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter and paginate users
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      // Search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          user.first_name.toLowerCase().includes(searchLower) ||
          user.last_name.toLowerCase().includes(searchLower) ||
          user.email.toLowerCase().includes(searchLower);

        if (!matchesSearch) {
return false;
}
      }

      // Status filter
      if (filters.status !== 'all') {
        switch (filters.status) {
          case 'active':
            // Active means they have some compensation set
            if (!user.maximum_compensation && !user.banked_compensation) {
return false;
}
            break;
          case 'mentorship':
            if (!user.mentorship_active) {
return false;
}
            break;
          case 'no_mentor':
            if (user.mentor_id !== null) {
return false;
}
            break;
          case 'inactive':
            // Inactive means no compensation set
            if (user.maximum_compensation || user.banked_compensation) {
return false;
}
            break;
        }
      }

      // Tier filter (by max compensation)
      if (filters.tier !== 'all') {
        const maxComp = user.maximum_compensation || 0;
        switch (filters.tier) {
          case 'tier1':
            if (maxComp <= 40000) {
return false;
}
            break;
          case 'tier2':
            if (maxComp <= 30000 || maxComp > 40000) {
return false;
}
            break;
          case 'tier3':
            if (maxComp <= 0 || maxComp > 30000) {
return false;
}
            break;
          case 'no_comp':
            if (maxComp > 0) {
return false;
}
            break;
        }
      }

      // Relationship filter
      if (filters.relationship !== 'all') {
        switch (filters.relationship) {
          case 'has_override':
            if (!user.override_recipient_id) {
return false;
}
            break;
          case 'is_mentor':
            // Check if this user is a mentor to anyone
          {
            const isMentor = users.some(u => u.mentor_id === user.id);
            if (!isMentor) {
              return false;
            }
            break;
          }
          case 'has_mentor':
            if (!user.mentor_id) {
return false;
}
            break;
          case 'independent':
            if (user.mentor_id || user.override_recipient_id) {
return false;
}
            break;
        }
      }

      return true;
    });
  }, [users, filters]);

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleEdit = (user: UserCompensation) => {
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  const handleDrawerSave = () => {
    loadUsers();
  };

  const handleSave = async () => {
    if (!editDialog) {
return;
}

    try {
      setSaving(true);

      // Routes through the comp-set edge function → rpc_comp_set. The trigger on `users`
      // (enforce_comp_write_authorization) blocks direct .update of guarded columns.
      await compSet({
        userId: editDialog.userId,
        maximum_compensation: editDialog.maximum_compensation,
        banked_compensation: editDialog.banked_compensation,
        broker_commission_split: editDialog.broker_commission_split,
        banked_override_bps: editDialog.banked_override_bps,
        broker_mentor_commission_split: editDialog.broker_mentor_commission_split,
        override_recipient_id: editDialog.override_recipient_id,
        mentor_id: editDialog.mentor_id,
        mentorship_active: editDialog.mentor_id !== null
      });

      toast.success('Compensation updated successfully');
      log.info('User compensation updated', { userId: editDialog.userId });

      setEditDialog(null);
      loadUsers();
    } catch (error) {
      log.error('Failed to update compensation', { userId: editDialog.userId }, error as Error);
      toast.error('Failed to update compensation', {
        description: (error as Error).message
      });
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) {
return '-';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value: number | null) => {
    if (value === null) {
return '-';
}
    return `${value.toFixed(2)}%`;
  };

  const formatBps = (value: number | null) => {
    if (value === null) {
return '-';
}
    return `${value} bps`;
  };

  // Refetch payouts after edit
  const refetchPayouts = async () => {
    try {
      const { data, error } = await getCompensationPayouts();

      if (error) {
        throw error;
      }

      const transformedPayouts: PayoutData[] = ((data || []) as PayoutRecord[]).filter(r => {
        if (r.payout_type === 'banked') {
          return r.fund_date !== null && r.fund_date !== undefined && r.banker_recon?.investor_purchase_date !== null && r.banker_recon?.investor_purchase_date !== undefined;
        }
        return r.fund_date !== null && r.fund_date !== undefined;
      }).map(mapRecordToPayout);

      await enrichWithReconDocs(transformedPayouts);
      setPayouts(transformedPayouts);
      const batches = await loadPaymentBatches();
      return { payouts: transformedPayouts, batches };
    } catch (error) {
      log.error('Failed to refetch payouts', {}, error as Error);
      return null;
    }
  };

  const openPaymentEmailDialog = (
    batch: PayoutPaymentBatch,
    currentPayouts: PayoutData[] = payouts
  ) => {
    const preview = buildPayoutPaymentEmailPreview(batch, currentPayouts);
    if (!preview) {
      toast.error('Payment email is unavailable', {
        description: batch.historyComplete
          ? 'The payment allocations are not currently linked to active payout records.'
          : 'Legacy payment history must be reconciled before it can be emailed.'
      });
      return;
    }
    setPaymentEmailDialog(preview);
  };

  const handleSendPaymentEmail = async () => {
    if (!paymentEmailDialog || paymentEmailDialog.paymentEmailSentAt) {
      return;
    }

    try {
      setSendingPaymentEmail(true);
      const result = await sendPayoutPaymentConfirmation(paymentEmailDialog.batchId);
      setPaymentEmailDialog((current) => current ? {
        ...current,
        paymentEmailSentAt: result.sentAt,
        paymentEmailSentByName: authUser?.fullName || authUser?.email || 'Unknown user',
        paymentEmailRecipient: result.recipientEmail
      } : null);
      setPaymentBatches((current) => current.map((batch) => (
        batch.id === paymentEmailDialog.batchId
          ? {
            ...batch,
            paymentEmailSentAt: result.sentAt,
            paymentEmailSentByName: authUser?.fullName || authUser?.email || 'Unknown user',
            paymentEmailRecipient: result.recipientEmail,
            paymentEmailResendId: result.emailId
          }
          : batch
      )));

      if (result.auditLogged) {
        toast.success('Payment email sent', {
          description: `Sent to ${paymentEmailDialog.recipientName} and recorded in Company Notifications.`
        });
      } else {
        toast.warning('Payment email sent', {
          description: 'Resend accepted the email, but the Company Notifications audit log needs review.'
        });
      }
      log.info('LO payment confirmation sent', {
        operation: 'send_payment_confirmation',
        metadata: {
          batchId: paymentEmailDialog.batchId,
          resendId: result.emailId,
          auditLogged: result.auditLogged
        }
      });
    } catch (error) {
      log.error('Failed to send LO payment confirmation', {
        operation: 'send_payment_confirmation',
        metadata: { batchId: paymentEmailDialog.batchId }
      }, error as Error);
      toast.error('Payment email was not sent', {
        description: (error as Error).message
      });
    } finally {
      setSendingPaymentEmail(false);
    }
  };

  const visiblePaymentBatches = useMemo(() => filterAndSortPaymentBatches(
    paymentBatches,
    paymentHistorySearch,
    paymentHistoryStatus,
    paymentHistorySort,
    paymentHistorySortDirection
  ), [
    paymentBatches,
    paymentHistorySearch,
    paymentHistoryStatus,
    paymentHistorySort,
    paymentHistorySortDirection
  ]);

  const handlePaymentHistorySort = (field: PaymentBatchSortField) => {
    if (paymentHistorySort === field) {
      setPaymentHistorySortDirection((current) => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setPaymentHistorySort(field);
    setPaymentHistorySortDirection(field === 'paymentDate' ? 'desc' : 'asc');
  };

  const togglePaymentBatchExpansion = (batchId: string) => {
    setExpandedPaymentBatches((current) => {
      const next = new Set(current);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  // Save payout LO assignment change with commission recalculation
  const handleSavePayoutEdit = async () => {
    if (!editPayoutDialog) {
return;
}

    // Only brokered payouts support LO reassignment with commission recalc
    if (editPayoutDialog.payout.type !== 'Brokered') {
      toast.error('Cannot edit this payout type', {
        description: `${editPayoutDialog.payout.type} payouts cannot be reassigned.`
      });
      setEditPayoutDialog(null);
      return;
    }

    // Mentor-split payouts can't be safely reassigned here: the LO row stores only the LO's E&O share,
    // and there's a companion mentor payout row this single-row recalc would leave untouched. Block and
    // route to Broker Recon. Use both signals (splits_description can be missing/stale).
    const isMentored = (editPayoutDialog.payout.splits?.toLowerCase().includes('mentor') ?? false)
      || mentorByLoan.has(editPayoutDialog.payout.loanNumber);
    if (isMentored) {
      toast.error('Mentor-split payouts must be re-reconciled in Broker Recon', {
        description: 'Reassigning a mentored payout would mis-handle the LO/mentor E&O split and the mentor row.'
      });
      setEditPayoutDialog(null);
      return;
    }

    try {
      setSavingPayout(true);

      // Fetch the new LO's compensation settings
      const { data: newLO, error: loError } = await supabase
        .from('users')
        .select('id, first_name, last_name')
        .eq('id', editPayoutDialog.newRecipientId)
        .single();

      if (loError || !newLO) {
        throw new Error('Failed to fetch new loan officer details');
      }

      // Get the current payout details for recalculation
      const { data: currentPayout, error: payoutError } = await supabase
        .from('payouts')
        .select('gross_check_received, processing_fee, e_and_o_deduction, lo_reimbursements_total, fund_date')
        .eq('id', editPayoutDialog.payout.id)
        .single();

      if (payoutError || !currentPayout) {
        throw new Error('Failed to fetch payout details');
      }

      // A reassignment changes the recipient, not the loan's funding-date basis.
      // Never substitute today's (or a scheduled future) user summary here.
      const splitResolution = await resolveBrokerCommissionSplit(
        editPayoutDialog.newRecipientId,
        currentPayout.fund_date ?? ''
      );
      const newBrokerSplit = splitResolution.brokerCommissionSplit;
      const grossCheck = Number(currentPayout.gross_check_received) || 0;
      const processingFee = Number(currentPayout.processing_fee) || 750; // broker recon processing fee is $750
      const eAndO = Number(currentPayout.e_and_o_deduction) || 50;
      const loReimbursements = Number(currentPayout.lo_reimbursements_total) || 0;

      // Calculate commission breakdown. E&O is NOT pre-split — it is deducted post-split in net_amount
      // below and collected via the company add-back; removing it here keeps the recon balanced.
      const netForSplit = grossCheck - processingFee - loReimbursements;
      const loCommissionBase = Math.round((netForSplit * (newBrokerSplit / 100)) * 100) / 100;
      const loTotalCommission = Math.round((loCommissionBase + loReimbursements) * 100) / 100;
      // Residual (not an independent multiply) so LO + company bases sum to netForSplit penny-exact.
      const companyCommissionBase = Math.round((netForSplit - loCommissionBase) * 100) / 100;
      const companyTotalRevenue = Math.round((companyCommissionBase + processingFee + eAndO) * 100) / 100;

      // Update payout with new LO and recalculated amounts
      const { error } = await supabase
        .from('payouts')
        .update({
          recipient_id: editPayoutDialog.newRecipientId,
          broker_split_percent: newBrokerSplit,
          broker_split_history_id: splitResolution.historyId,
          broker_split_effective_date: splitResolution.effectiveDate,
          lo_commission_base: loCommissionBase,
          gross_amount: loTotalCommission,
          net_amount: Math.round((loTotalCommission - eAndO) * 100) / 100,
          company_commission_base: companyCommissionBase,
          company_total_revenue: companyTotalRevenue,
          updated_at: new Date().toISOString()
        })
        .eq('id', editPayoutDialog.payout.id);

      if (error) {
throw error;
}

      const oldLoName = editPayoutDialog.payout.loName;
      const newLoName = `${newLO.first_name} ${newLO.last_name}`;

      toast.success('Payout updated', {
        description: `Changed from ${oldLoName} to ${newLoName}. Commission recalculated at ${newBrokerSplit}% split.`
      });

      setEditPayoutDialog(null);
      await refetchPayouts();
    } catch (error) {
      log.error('Failed to update payout', { payoutId: editPayoutDialog.payout.id }, error as Error);
      toast.error('Failed to update payout', {
        description: (error as Error).message
      });
    } finally {
      setSavingPayout(false);
    }
  };

  // Save payment record
  const handleSavePayment = async () => {
    if (!paymentDialog) {
return;
}

    try {
      setSavingPayment(true);
      const payout = paymentDialog.payout;
      const validation = validatePaymentAmount(paymentDialog.paymentAmount);
      if (!validation.valid) {
        toast.error('Invalid payment amount', { description: validation.error });
        setSavingPayment(false);
        return;
      }
      if (!paymentDialog.checkNumber.trim() || !paymentDialog.paymentDate) {
        toast.error('Check number and payment date are required');
        setSavingPayment(false);
        return;
      }
      const enteredAmount = validation.amount;
      const newTotal = Math.round(((payout.paymentAmount || 0) + enteredAmount) * 100) / 100;
      const isFullyPaid = !isPayoutOutstanding(payout.netPayout, newTotal, payout.recapturedAmount, payout.type);

      const recordedPayment = await recordPayoutPaymentBatch({
        checkNumber: paymentDialog.checkNumber,
        paymentDate: paymentDialog.paymentDate,
        memo: paymentDialog.memo,
        idempotencyKey: paymentDialog.idempotencyKey,
        allocations: [{ payoutId: payout.id, amount: enteredAmount }]
      });

      const remaining = computeBalance(payout.netPayout, newTotal, payout.recapturedAmount);
      toast.success(isFullyPaid ? 'Payment recorded — settled' : `Partial payment recorded — $${remaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} remaining`, {
        description: `Check #${paymentDialog.checkNumber} for $${enteredAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
      });

      const refreshed = await refetchPayouts();
      const batch = refreshed?.batches.find((candidate) => candidate.id === recordedPayment.batchId);
      if (batch && refreshed) {
        openPaymentEmailDialog(batch, refreshed.payouts);
      }
      // Keep the payment dialog mounted through the refresh, then hand off both
      // dialog states in one render so the page never flashes between steps.
      setPaymentDialog(null);
    } catch (error) {
      log.error('Failed to record payment', { payoutId: paymentDialog.payout.id }, error as Error);
      toast.error('Failed to record payment', {
        description: (error as Error).message
      });
    } finally {
      setSavingPayment(false);
    }
  };

  // Save bulk payment record
  const handleSaveBulkPayment = async () => {
    if (!bulkPaymentDialog || bulkPaymentDialog.payouts.length === 0) {
return;
}

    try {
      setSavingPayment(true);

      // Validate: no negative or zero amounts
      if (hasInvalidBulkPaymentAmounts(bulkPaymentDialog.payouts.map(p => p.enteredAmount))) {
        const invalidCount = bulkPaymentDialog.payouts.filter(p => !validatePaymentAmount(p.enteredAmount).valid).length;
        toast.error('Invalid payment amounts', {
          description: `${invalidCount} payout(s) have invalid amounts`
        });
        setSavingPayment(false);
        return;
      }

      const totalEntered = bulkPaymentDialog.payouts.reduce((sum, p) => sum + parseFloat(p.enteredAmount), 0);

      if (!bulkPaymentDialog.checkNumber.trim() || !bulkPaymentDialog.paymentDate) {
        toast.error('Check number and payment date are required');
        setSavingPayment(false);
        return;
      }

      const recordedPayment = await recordPayoutPaymentBatch({
        checkNumber: bulkPaymentDialog.checkNumber,
        paymentDate: bulkPaymentDialog.paymentDate,
        memo: bulkPaymentDialog.memo,
        idempotencyKey: bulkPaymentDialog.idempotencyKey,
        allocations: bulkPaymentDialog.payouts.map((payout) => ({
          payoutId: payout.id,
          amount: parseFloat(payout.enteredAmount)
        }))
      });

      toast.success('Bulk payment recorded', {
        description: `Check #${bulkPaymentDialog.checkNumber} for ${bulkPaymentDialog.payouts.length} payouts totaling $${totalEntered.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
      });

      const refreshed = await refetchPayouts();
      const batch = refreshed?.batches.find((candidate) => candidate.id === recordedPayment.batchId);
      if (batch && refreshed) {
        openPaymentEmailDialog(batch, refreshed.payouts);
      }
      // Keep the bulk dialog mounted through the refresh and transition directly
      // into the optional email review without closing back to the page.
      setBulkPaymentDialog(null);
      setSelectedForPayment(new Set());
    } catch (error) {
      log.error('Failed to record bulk payment', { count: bulkPaymentDialog.payouts.length }, error as Error);
      toast.error('Failed to record bulk payment', {
        description: (error as Error).message
      });
    } finally {
      setSavingPayment(false);
    }
  };

  // Void the entire physical check while retaining its immutable allocations.
  const handleVoidPayment = async () => {
    if (!voidPaymentDialog) {
      return;
    }
    const trimmedReason = voidPaymentDialog.reason.trim();
    if (!trimmedReason) {
      return;
    }

    try {
      setSavingPayment(true);
      await voidPayoutPaymentBatch(voidPaymentDialog.batch.id, trimmedReason);

      toast.success(`Voided check #${voidPaymentDialog.batch.checkNumber}`, {
        description: `${voidPaymentDialog.batch.allocations.length} allocation${voidPaymentDialog.batch.allocations.length !== 1 ? 's' : ''} removed from active paid totals`
      });
      setVoidPaymentDialog(null);
      await refetchPayouts();
    } catch (error) {
      log.error('Failed to void payment check', { batchId: voidPaymentDialog.batch.id }, error as Error);
      toast.error('Failed to void payment check', {
        description: (error as Error).message
      });
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">User Compensation</h1>
        <p className="text-muted-foreground">Manage compensation settings for loan officers</p>
      </div>

      {/* Top-level tabs for Users vs Summary vs Account vs Revenue vs OB Plans */}
      {recaptureError && <p role="status" className="text-sm text-destructive">{recaptureError}</p>}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="summary" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="account" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Accounting
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Company Revenue
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <Wallet className="h-4 w-4" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="balances" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Balances
          </TabsTrigger>
          <TabsTrigger value="ob_plans" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            OB Plans
          </TabsTrigger>
          {showRecapture && <TabsTrigger value="recapture">Recapture</TabsTrigger>}
        </TabsList>

        {showRecapture && recaptureWorkspace && <TabsContent value="recapture" className="mt-6 min-w-0">
          <RecapturePanel workspace={recaptureWorkspace} onRefresh={async () => {
            await refreshRecaptures();
            await refetchPayouts();
          }} />
        </TabsContent>}

        {/* OB Plans Tab Content — admin toggles is_selectable_for_lo_self_service per schedule */}
        <TabsContent value="ob_plans" className="mt-6 space-y-6">
          <ObSelectablePlansPanel />
        </TabsContent>

        {/* Users Tab Content */}
        <TabsContent value="users" className="mt-6 space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Loan Officers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Compensation</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.maximum_compensation !== null || u.broker_commission_split !== null).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Mentorships</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(u => u.mentorship_active).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <CompensationFilters
        filters={filters}
        onFilterChange={setFilters}
        onExport={() => {
          // TODO: Implement CSV export
          toast.info('Export feature coming soon');
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>Loan Officer Compensation Settings</CardTitle>
          <CardDescription>
            Configure compensation structure for each loan officer
            {filteredUsers.length < users.length && (
              <span className="ml-2 text-sm font-normal">
                (showing {filteredUsers.length} of {users.length})
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading loan officers...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Max Comp</TableHead>
                  <TableHead className="text-right">Banked %</TableHead>
                  <TableHead className="text-right">Broker Split %</TableHead>
                  <TableHead className="text-right">Override</TableHead>
                  <TableHead className="text-right">Mentor Split %</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      {filters.searchTerm || filters.status !== 'all' || filters.tier !== 'all' || filters.relationship !== 'all'
                        ? 'No users match the selected filters'
                        : 'No loan officers found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleEdit(user)}
                    >
                      <TableCell className="font-medium">
                        <div>
                          <div>{user.first_name} {user.last_name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          <PendingRecaptureNotice records={recaptureWarnings} recipientId={user.id} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {user.roles?.includes('loan_officer') ? 'Loan Officer' : user.roles?.[0] || 'No Role'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(user.maximum_compensation)}</TableCell>
                      <TableCell className="text-right">{formatPercentage(user.banked_compensation)}</TableCell>
                      <TableCell className="text-right">{formatPercentage(user.broker_commission_split)}</TableCell>
                      <TableCell className="text-right">{formatBps(user.banked_override_bps)}</TableCell>
                      <TableCell className="text-right">{formatPercentage(user.broker_mentor_commission_split)}</TableCell>
                      <TableCell className="text-center">
                        {user.mentorship_active && (
                          <Badge variant="secondary">
                            Mentorship {user.brokered_deals_completed}/5
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <CompensationRowActions
                          user={user}
                          onEdit={handleEdit}
                          onViewHistory={(_user) => {
                            // Phase 3: Open drawer to history tab
                            toast.info('View History feature coming in Phase 3');
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          {/* Pagination Controls */}
          {!loading && filteredUsers.length > 0 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="text-sm font-medium">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Summary Tab Content */}
        <TabsContent value="summary" className="mt-6 space-y-6">
          {/* Unified Summary Stats Card */}
          <Card>
            <CardContent className="px-5 py-4 space-y-3">
              {/* Brokered Loans Section */}
              {summaryStats.brokeredUnits > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Brokered Loans</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-1">
                    <div>
                      <div className="text-xs text-muted-foreground">Gross Checks</div>
                      <div className="text-sm font-semibold tabular-nums">${summaryStats.brokeredGrossChecks.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">LO Commissions</div>
                      <div className="text-sm font-semibold text-green-600 tabular-nums">${summaryStats.brokeredCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Company Revenue</div>
                      <div className="text-sm font-semibold text-green-700 tabular-nums">${summaryStats.brokeredCompanyRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Units</div>
                      <div className="text-sm font-semibold tabular-nums">{summaryStats.brokeredUnits}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Total Loan Amt</div>
                      <div className="text-sm font-semibold tabular-nums">${summaryStats.brokeredTotalLoanAmt.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Separator between Brokered and Banked */}
              {summaryStats.brokeredUnits > 0 && summaryStats.bankedUnits > 0 && <Separator />}

              {/* Banked Loans Section */}
              {summaryStats.bankedUnits > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Banked Loans</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-1">
                    <div>
                      <div className="text-xs text-muted-foreground">Company Revenue</div>
                      <div className="text-sm font-semibold tabular-nums">${summaryStats.bankedCompanyRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">LO Commissions</div>
                      <div className="text-sm font-semibold text-blue-600 tabular-nums">${summaryStats.bankedLoCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Company Net Profit</div>
                      <div className="text-sm font-semibold text-blue-700 tabular-nums">${summaryStats.bankedNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Units</div>
                      <div className="text-sm font-semibold tabular-nums">{summaryStats.bankedUnits}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Total Loan Amt</div>
                      <div className="text-sm font-semibold tabular-nums">${summaryStats.bankedTotalLoanAmt.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Separator before Mentor/Override */}
              {summaryStats.volumeBonusCount > 0 && <p className="text-sm tabular-nums">
                Volume bonuses: <strong>{fmtCurrency(summaryStats.volumeBonusTotal)}</strong> · {summaryStats.volumeBonusCount} installment{summaryStats.volumeBonusCount === 1 ? '' : 's'}
              </p>}
              {(summaryStats.brokeredUnits > 0 || summaryStats.bankedUnits > 0) && (summaryStats.mentorUnits > 0 || summaryStats.overrideUnits > 0) && <Separator />}

              {/* Mentor + Override Inline Row */}
              {(summaryStats.mentorUnits > 0 || summaryStats.overrideUnits > 0) && (
                <div className="flex items-center gap-6 flex-wrap">
                  {summaryStats.mentorUnits > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-purple-500" />
                      <span className="text-xs font-medium text-purple-700">Mentor:</span>
                      <span className="text-sm font-semibold text-purple-600 tabular-nums">${summaryStats.mentorCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-xs text-muted-foreground">({summaryStats.mentorUnits} units)</span>
                    </div>
                  )}
                  {summaryStats.overrideUnits > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-orange-500" />
                      <span className="text-xs font-medium text-orange-700">Override:</span>
                      <span className="text-sm font-semibold text-orange-600 tabular-nums">${summaryStats.overrideCommissions.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-xs text-muted-foreground">({summaryStats.overrideUnits} units)</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Grouping Controls */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">Group by:</span>
            <div className="flex gap-2">
              <Button
                variant={summaryGrouping === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSummaryGrouping('all')}
              >
                <FileText className="h-4 w-4 mr-2" />
                All
              </Button>
              <Button
                variant={summaryGrouping === 'lo' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSummaryGrouping('lo')}
              >
                <User className="h-4 w-4 mr-2" />
                By LO
              </Button>
              <Button
                variant={summaryGrouping === 'month' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSummaryGrouping('month')}
              >
                <Calendar className="h-4 w-4 mr-2" />
                By Month
              </Button>
            </div>
          </div>

          {/* Payouts Table(s) - Split by Brokered/Banked */}
          {Object.entries(groupedPayouts).map(([groupName, groupPayouts]) => {
            const brokeredPayouts = groupPayouts.filter(p => p.type === 'Brokered');
            const bankedPayouts = groupPayouts.filter(p => p.type === 'Banked');
            const overridePayouts = groupPayouts.filter(p => p.type === 'Override');
            const volumeBonusPayouts = groupPayouts.filter(p => p.type === 'Volume Bonus');
            const mentorPayoutsGroup = groupPayouts.filter(p => p.type === 'Mentor');
            const hasMultipleSections = [brokeredPayouts.length, bankedPayouts.length + overridePayouts.length, mentorPayoutsGroup.length].filter(n => n > 0).length > 1;
            const brokeredTotal = brokeredPayouts.reduce((sum, p) => sum + p.netPayout, 0);
            const bankedTotal = bankedPayouts.reduce((sum, p) => sum + p.netPayout, 0);
            const overrideTotal = overridePayouts.reduce((sum, p) => sum + p.netPayout, 0);
            const mentorGroupTotal = mentorPayoutsGroup.reduce((sum, p) => sum + p.netPayout, 0);

            // Shared edit cells renderer

            const renderEditCell = (payout: PayoutData) => (
              <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                {payout.type === 'Brokered' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => setEditPayoutDialog({
                      payout,
                      newRecipientId: payout.loId
                    })}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                ) : (
                  <span className="text-muted-foreground">&mdash;</span>
                )}
              </TableCell>
            );

            return (
              <Card key={groupName}>
                <CardHeader>
                  <CardTitle className="text-lg">{groupName}</CardTitle>
                  <CardDescription className="flex flex-wrap gap-4">
                    {brokeredPayouts.length > 0 && (
                      <span className="text-green-700">
                        Brokered LO Commissions: ${brokeredTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                    {bankedPayouts.length > 0 && (
                      <span className="text-blue-700">
                        Banked LO Payouts: ${bankedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                    {mentorPayoutsGroup.length > 0 && (
                      <span className="text-purple-700">
                        Mentor Commissions: ${mentorGroupTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Brokered Table */}
                  {brokeredPayouts.length > 0 && (
                    <div>
                      {hasMultipleSections && <div className="text-sm font-semibold text-green-700 mb-2">BROKERED</div>}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <SortableHeader field="loanNumber">Loan #</SortableHeader>
                            <TableHead>Type</TableHead>
                            <SortableHeader field="loName">LO</SortableHeader>
                            <SortableHeader field="loanAmount" align="right">Loan Amount</SortableHeader>
                            <SortableHeader field="grossPayout" align="right">Gross</SortableHeader>
                            <TableHead className="text-right">Processing</TableHead>
                            <SortableHeader field="eAndO" align="right">E&O</SortableHeader>
                            <TableHead className="text-right">Reimb</TableHead>
                            <TableHead>Split</TableHead>
                            <SortableHeader field="netPayout" align="right">Net Payout</SortableHeader>
                            <SortableHeader field="date">Date</SortableHeader>
                            <TableHead className="text-center">Attachments</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {brokeredPayouts.map((payout, index) => {
                            const prevPayout = index > 0 ? brokeredPayouts[index - 1] : null;
                            const isSameLoanAsPrev = prevPayout && prevPayout.loanNumber === payout.loanNumber;
                            const isExpanded = expandedRows.has(payout.id);
                            return (
                              <React.Fragment key={payout.id}>
                                <TableRow
                                  className={`cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                                  onClick={() => toggleRowExpansion(payout.id)}
                                >
                                  <TableCell className="w-8 p-2">
                                    {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {isSameLoanAsPrev ? (
                                      <span className="text-muted-foreground">↳</span>
                                    ) : (
                                      <div>
                                        <div>{payout.loanNumber}</div>
                                        {payout.borrowerName && <div className="text-sm text-muted-foreground font-normal">{payout.borrowerName}</div>}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className={
                                      payout.type === 'Brokered' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                      payout.type === 'Override' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                      payout.type === 'Mentor' ? 'bg-purple-100 text-purple-800 border-purple-300' : ''
                                    }>
                                      {payout.type}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>{payout.loName}</TableCell>
                                  <TableCell className="text-right">
                                    {isSameLoanAsPrev ? <span className="text-muted-foreground">-</span> : `$${payout.loanAmount.toLocaleString()}`}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    ${payout.grossCheckReceived.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right">{payout.processingFee > 0 ? `-$${payout.processingFee.toLocaleString()}` : '-'}</TableCell>
                                  <TableCell className="text-right">{payout.eAndO > 0 ? `-$${payout.eAndO}` : '-'}</TableCell>
                                  <TableCell className="text-right">{payout.loReimbursementsTotal > 0 ? `-$${payout.loReimbursementsTotal.toLocaleString()}` : '-'}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{payout.splits || '-'}</TableCell>
                                  <TableCell className="text-right font-medium text-green-600">
                                    ${payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {isSameLoanAsPrev ? <span className="text-muted-foreground/50">″</span> : payout.date}
                                  </TableCell>
                                  {renderAttachmentCell(payout)}
                                  {renderEditCell(payout)}
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={14} className="p-4">
                                      <CalculationBreakdown payout={payout} />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Banked Table */}
                  {bankedPayouts.length > 0 && (
                    <div>
                      {hasMultipleSections && <div className="text-sm font-semibold text-blue-700 mb-2">BANKED</div>}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <SortableHeader field="loanNumber">Loan #</SortableHeader>
                            <SortableHeader field="loName">LO</SortableHeader>
                            <SortableHeader field="loanAmount" align="right">Loan Amount</SortableHeader>
                            <TableHead className="text-right">Company Revenue</TableHead>
                            <TableHead className="text-right">LO Gross</TableHead>
                            <SortableHeader field="eAndO" align="right">E&O</SortableHeader>
                            <SortableHeader field="netPayout" align="right">Net Payout</SortableHeader>
                            <SortableHeader field="date">Date</SortableHeader>
                            <TableHead className="text-center">Attachments</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bankedPayouts.map((payout, index) => {
                            const prevPayout = index > 0 ? bankedPayouts[index - 1] : null;
                            const isSameLoanAsPrev = prevPayout && prevPayout.loanNumber === payout.loanNumber;
                            const isExpanded = expandedRows.has(payout.id);
                            return (
                              <React.Fragment key={payout.id}>
                                <TableRow
                                  className={`cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                                  onClick={() => toggleRowExpansion(payout.id)}
                                >
                                  <TableCell className="w-8 p-2">
                                    {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {isSameLoanAsPrev ? (
                                      <span className="text-muted-foreground">↳</span>
                                    ) : (
                                      <div>
                                        <div>{payout.loanNumber}</div>
                                        {payout.borrowerName && <div className="text-sm text-muted-foreground font-normal">{payout.borrowerName}</div>}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>{payout.loName}</TableCell>
                                  <TableCell className="text-right">
                                    {isSameLoanAsPrev ? <span className="text-muted-foreground">-</span> : `$${payout.loanAmount.toLocaleString()}`}
                                  </TableCell>
                                  <TableCell className="text-right text-blue-700">
                                    ${(payout.companyRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    ${(payout.grossPayout || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right">{payout.eAndO > 0 ? `-$${payout.eAndO}` : '-'}</TableCell>
                                  <TableCell className="text-right font-medium text-green-600">
                                    ${payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {isSameLoanAsPrev ? <span className="text-muted-foreground/50">″</span> : payout.date}
                                  </TableCell>
                                  {renderAttachmentCell(payout)}
                                  {renderEditCell(payout)}
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={11} className="p-4">
                                      <CalculationBreakdown payout={payout} />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Override Table (Banked family) */}
                  {volumeBonusPayouts.length > 0 && <div className="min-w-0">
                    <h3 className="mb-2 text-sm font-semibold">Volume bonuses</h3>
                    <Table aria-label="Volume bonus payouts" containerProps={{ role: 'region', 'aria-label': 'Volume bonus payouts table', tabIndex: 0 }}>
                      <TableHeader><TableRow>
                        <TableHead>Loan</TableHead><TableHead>Loan officer</TableHead><TableHead>Installment</TableHead>
                        <TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Cash paid</TableHead><TableHead className="text-right">Remaining</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>{volumeBonusPayouts.map(payout => <TableRow key={payout.id}>
                        <TableCell>{payout.loanNumber}</TableCell><TableCell>{payout.loName}</TableCell><TableCell>{payout.splits || 'Volume Bonus'}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCurrency(payout.netPayout)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCurrency(payout.paymentAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCurrency(computeBalance(payout.netPayout, payout.paymentAmount))}</TableCell>
                      </TableRow>)}</TableBody>
                    </Table>
                  </div>}
                  {overridePayouts.length > 0 && (
                    <div>
                      {hasMultipleSections && <div className="text-sm font-semibold text-orange-700 mb-2">BANKED OVERRIDES</div>}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <TableHead>Loan #</TableHead>
                            <TableHead>Source LO</TableHead>
                            <TableHead className="text-right">Loan Amount</TableHead>
                            <TableHead className="text-right">Override</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {overridePayouts.map(payout => {
                            const isExpanded = expandedRows.has(payout.id);
                            return (
                              <React.Fragment key={payout.id}>
                                <TableRow
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => setExpandedRows(prev => {
                                    const next = new Set(prev);
                                    if (next.has(payout.id)) {
                                      next.delete(payout.id);
                                    } else {
                                      next.add(payout.id);
                                    }
                                    return next;
                                  })}
                                >
                                  <TableCell className="w-8 text-muted-foreground">{isExpanded ? '▼' : '▶'}</TableCell>
                                  <TableCell className="font-medium">{payout.loanNumber}</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{payout.source}</TableCell>
                                  <TableCell className="text-right font-mono">${payout.loanAmount.toLocaleString()}</TableCell>
                                  <TableCell className="text-right font-mono font-bold text-orange-700">${payout.grossPayout.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">Override</Badge>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{payout.date}</TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={7} className="p-4">
                                      <CalculationBreakdown payout={payout} />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                      {overridePayouts.length > 0 && (
                        <div className="text-right text-sm font-semibold text-orange-700 mt-1 pr-4">
                          Total Overrides: ${overrideTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Mentor Table */}
                  {mentorPayoutsGroup.length > 0 && (
                    <div>
                      {hasMultipleSections && <div className="text-sm font-semibold text-purple-700 mb-2">MENTOR COMMISSIONS</div>}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <TableHead>Loan #</TableHead>
                            <TableHead>Source LO</TableHead>
                            <SortableHeader field="loanAmount" align="right">Loan Amount</SortableHeader>
                            <TableHead>Split %</TableHead>
                            <SortableHeader field="grossPayout" align="right">Gross</SortableHeader>
                            <SortableHeader field="eAndO" align="right">E&O</SortableHeader>
                            <SortableHeader field="netPayout" align="right">Net Payout</SortableHeader>
                            <SortableHeader field="date">Date</SortableHeader>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mentorPayoutsGroup.map((payout) => {
                            const isExpanded = expandedRows.has(payout.id);
                            return (
                              <React.Fragment key={payout.id}>
                                <TableRow
                                  className={`cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                                  onClick={() => toggleRowExpansion(payout.id)}
                                >
                                  <TableCell className="w-8 p-2">
                                    {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div>
                                      <div>{payout.loanNumber}</div>
                                      {payout.borrowerName && <div className="text-sm text-muted-foreground font-normal">{payout.borrowerName}</div>}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">{payout.source || '-'}</div>
                                  </TableCell>
                                  <TableCell className="text-right">${payout.loanAmount.toLocaleString()}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                                      {payout.brokerSplitPercent}%
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    ${payout.grossPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right">{payout.eAndO > 0 ? `-$${payout.eAndO}` : '-'}</TableCell>
                                  <TableCell className="text-right font-medium text-purple-600">
                                    ${payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{payout.date}</TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={9} className="p-4">
                                      <CalculationBreakdown payout={payout} />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Legend for capped payouts */}
          {payouts.some(p => p.hitCap) && (
            <div className="p-3 border rounded-lg bg-muted/30 text-sm">
              <span className="font-medium text-blue-800">*</span>
              <span className="text-muted-foreground ml-1">
                Gross amount hit maximum compensation cap. The loan officer's calculated commission exceeded their cap limit, so they received the capped amount instead.
              </span>
            </div>
          )}
        </TabsContent>

        {/* Account Tab Content - Payroll Processing */}
        <TabsContent value="account" className="mt-6 space-y-6">
          {/* Pay Period Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="pay-month" className="text-sm font-medium whitespace-nowrap">Pay Month:</Label>
              <Select value={selectedPayMonth} onValueChange={setSelectedPayMonth}>
                <SelectTrigger id="pay-month" className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Periods</SelectItem>
                  {monthOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-sm font-medium whitespace-nowrap">Pay Period:</Label>
              <div className="flex gap-1">
                <Button
                  variant={selectedPayPeriod === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPayPeriod('all')}
                >
                  All
                </Button>
                <Button
                  variant={selectedPayPeriod === '15th' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPayPeriod('15th')}
                >
                  15th
                </Button>
                <Button
                  variant={selectedPayPeriod === 'EOM' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPayPeriod('EOM')}
                >
                  EOM
                </Button>
              </div>
            </div>
          </div>

          {/* Payroll Summary Stats */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total payroll</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${payrollStats.totalToPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Includes paid and unpaid payouts for the selected period. See Payments or Balances for amounts still owed.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">E&O Collected</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${payrollStats.totalEandO}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Recipients</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{payrollStats.recipientCount}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Loans</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{payrollStats.loanCount}</div>
              </CardContent>
            </Card>
          </div>

          {/* Pay Period Info Banner */}
          <div className="p-4 border rounded-lg bg-muted/30">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Pay Period Rules</p>
                <p className="text-muted-foreground">
                  Loans funded <strong>1st - 14th</strong> of month → Pay on <strong>1st</strong> of next month
                </p>
                <p className="text-muted-foreground">
                  Loans funded <strong>15th - end</strong> of month → Pay on <strong>15th</strong> of next month
                </p>
              </div>
            </div>
          </div>

          {/* Payroll by Recipient */}
          {payrollByRecipient.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No payouts scheduled for this pay period
              </CardContent>
            </Card>
          ) : (
            payrollByRecipient.map(recipient => (
              <Card key={recipient.name}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="h-5 w-5" />
                        {recipient.name}
                      </CardTitle>
                      <CardDescription>
                        {recipient.payouts.length} payout{recipient.payouts.length !== 1 ? 's' : ''} from {new Set(recipient.payouts.map(p => p.loanNumber)).size} loan{new Set(recipient.payouts.map(p => p.loanNumber)).size !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        ${recipient.totalNet.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      {recipient.totalEandO > 0 && (
                        <div className="text-sm text-muted-foreground">
                          E&O: ${recipient.totalEandO}
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Loan #</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Fund Date</TableHead>
                        <TableHead>Pay Date</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">E&O</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Attachments</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recipient.payouts.map(payout => {
                        const isExpanded = expandedRows.has(payout.id);
                        return (
                          <React.Fragment key={payout.id}>
                            <TableRow
                              className={`cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                              onClick={() => toggleRowExpansion(payout.id)}
                            >
                              <TableCell className="w-8 p-2">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                <div>
                                  <div>{payout.loanNumber}</div>
                                  {payout.borrowerName && (
                                    <div className="text-sm text-muted-foreground font-normal">{payout.borrowerName}</div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <Badge variant="outline" className={
                                    payout.type === 'Banked' ? 'bg-green-100 text-green-800 border-green-300' :
                                    payout.type === 'Brokered' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                    payout.type === 'Override' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                    payout.type === 'Mentor' ? 'bg-purple-100 text-purple-800 border-purple-300' : ''
                                  }>
                                    {payout.type}
                                  </Badge>
                                  {payout.type === 'Brokered' && mentorByLoan.has(payout.loanNumber) && (
                                    <div className="text-xs text-purple-600 mt-1">
                                      Mentored by {mentorByLoan.get(payout.loanNumber)}
                                    </div>
                                  )}
                                  {payout.type === 'Mentor' && payout.source && (
                                    <div className="text-xs text-purple-600 mt-1">
                                      Mentoring {payout.source}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                {parseAsLocalDate(payout.fundDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="font-normal">
                                  {payout.payDateFormatted}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                ${payout.grossPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-right">
                                {payout.eAndO > 0 ? `-$${payout.eAndO}` : '-'}
                              </TableCell>
                              <TableCell className="text-right font-medium text-green-600">
                                ${payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </TableCell>
                              <TableCell className="text-center">
                                {payout.status === 'paid' ? (
                                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    {(payout.recapturedAmount || 0) > 0 ? 'Settled' : 'Paid'}
                                  </Badge>
                                ) : payout.status === 'approved' ? (
                                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Approved
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pending
                                  </Badge>
                                )}
                              </TableCell>
                              {renderAttachmentCell(payout)}
                            </TableRow>
                            {isExpanded && (
                              <TableRow className="bg-muted/20 hover:bg-muted/20">
                                <TableCell colSpan={10} className="p-4">
                                  <CalculationBreakdown payout={payout} showPayrollDetail />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))
          )}

          {/* Grand Total */}
          {payrollByRecipient.length > 0 && (
            <Card className="border-2 border-primary/20 bg-primary/5">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-primary" />
                    <span className="font-medium">
                      Total Payroll for {selectedPayPeriod === 'all' ? 'All Periods' : selectedPayPeriod}{selectedPayMonth === 'all' ? '' : ` of ${monthOptions.find(m => m.value === selectedPayMonth)?.label}`}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-600">
                      ${payrollStats.totalToPay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {payrollStats.recipientCount} recipient{payrollStats.recipientCount !== 1 ? 's' : ''} • {payrollStats.loanCount} loan{payrollStats.loanCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Company Revenue Tab Content */}
        <TabsContent value="revenue" className="mt-6 space-y-6">
          {/* Revenue Stats — Brokered & Banked side by side */}
          <div className="grid gap-4 md:grid-cols-2">
            {revenueStats.brokeredUnits > 0 && (
              <Card className="border-green-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Brokered Company Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600 mb-3">
                    ${revenueStats.brokeredRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Units</div>
                      <div className="font-semibold">{revenueStats.brokeredUnits}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Total Loan Amount</div>
                      <div className="font-semibold">${revenueStats.brokeredTotalLoanAmt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Avg Loan Amount</div>
                      <div className="font-semibold">${revenueStats.brokeredAvgLoan.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Avg Revenue/Loan</div>
                      <div className="font-semibold text-green-600">${revenueStats.brokeredAvgRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {revenueStats.bankedUnits > 0 && (
              <Card className="border-blue-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Banked Net Profit</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-blue-600 mb-3">
                    ${revenueStats.bankedNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Units</div>
                      <div className="font-semibold">{revenueStats.bankedUnits}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Total Loan Amount</div>
                      <div className="font-semibold">${revenueStats.bankedTotalLoanAmt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Avg Loan Amount</div>
                      <div className="font-semibold">${revenueStats.bankedAvgLoan.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Avg Net Profit/Loan</div>
                      <div className="font-semibold text-blue-600">${revenueStats.bankedAvgProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Period & Grouping Controls */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <ReportDateRangePicker value={revenuePeriod} onChange={setRevenuePeriod} />
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-muted-foreground">Group by:</span>
              <div className="flex gap-2">
                <Button
                  variant={revenueGrouping === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRevenueGrouping('all')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  All
                </Button>
                <Button
                  variant={revenueGrouping === 'lo' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRevenueGrouping('lo')}
                >
                  <User className="h-4 w-4 mr-2" />
                  By LO
                </Button>
                <Button
                  variant={revenueGrouping === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRevenueGrouping('month')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  By Month
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={revenueFilteredPayouts.length === 0 || payoutsLoading}
                onClick={() => {
                  const summaryRows = buildRevenueSummaryRows(revenueFilteredPayouts, computeBankedNetProfit);
                  const detailRows = buildRevenueDetailRows(revenueFilteredPayouts, computeBankedNetProfit);
                  const channelRows = buildChannelComparisonRows(revenueFilteredPayouts, computeBankedNetProfit);
                  const monthlyRows = buildMonthlyComparisonRows(revenueFilteredPayouts, computeBankedNetProfit);
                  exportRevenueWorkbook({ summaryRows, detailRows, channelRows, monthlyRows, periodLabel: revenuePeriodLabel });
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Revenue Table(s) */}
          {Object.entries(groupedRevenuePayouts).map(([groupName, groupPayouts]) => {
            const brokeredPayouts = groupPayouts.filter(p => p.type !== 'Banked' && p.type !== 'Mentor' && p.type !== 'Override');
            const bankedPayouts = groupPayouts.filter(p => p.type === 'Banked');
            const brokeredTotal = brokeredPayouts.reduce((sum, p) => sum + p.companyRevenue, 0);
            const bankedTotal = bankedPayouts.reduce((sum, p) => sum + computeBankedNetProfit(p), 0);
            const groupLoans = new Set(groupPayouts.map(p => p.loanNumber)).size;

            return (
              <Card key={groupName}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {revenueGrouping === 'lo' && <User className="h-5 w-5" />}
                        {revenueGrouping === 'month' && <Calendar className="h-5 w-5" />}
                        {groupName}
                      </CardTitle>
                      <CardDescription>
                        {groupLoans} loan{groupLoans !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                    <div className="text-right space-y-1">
                      {brokeredPayouts.length > 0 && (
                        <div>
                          <div className="text-2xl font-bold text-green-600">
                            ${brokeredTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Company Revenue
                          </div>
                        </div>
                      )}
                      {bankedPayouts.length > 0 && (
                        <div>
                          <div className={`${brokeredPayouts.length > 0 ? 'text-lg' : 'text-2xl'} font-bold text-blue-600`}>
                            ${bankedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Banked Net Profit
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* --- BROKERED LOANS TABLE --- */}
                  {brokeredPayouts.length > 0 && (
                    <div>
                      {bankedPayouts.length > 0 && (
                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Brokered</div>
                      )}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <SortableHeader field="loanNumber">Loan #</SortableHeader>
                            {revenueGrouping !== 'lo' && <SortableHeader field="loName">LO</SortableHeader>}
                            <SortableHeader field="loanAmount" align="right">Loan Amount</SortableHeader>
                            <TableHead className="text-right">Gross Check</TableHead>
                            <TableHead className="text-right">Processing</TableHead>
                            <TableHead className="text-right">E&O</TableHead>
                            <TableHead className="text-right">Company Revenue</TableHead>
                            <SortableHeader field="date">Date</SortableHeader>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {brokeredPayouts.map((payout) => {
                            const isExpanded = expandedRows.has(`rev-${payout.id}`);
                            return (
                              <React.Fragment key={payout.id}>
                                <TableRow
                                  className={`cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                                  onClick={() => toggleRowExpansion(`rev-${payout.id}`)}
                                >
                                  <TableCell className="w-8 p-2">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div>
                                      <div>{payout.loanNumber}</div>
                                      {payout.borrowerName && (
                                        <div className="text-sm text-muted-foreground font-normal">{payout.borrowerName}</div>
                                      )}
                                    </div>
                                  </TableCell>
                                  {revenueGrouping !== 'lo' && <TableCell>{payout.loName}</TableCell>}
                                  <TableCell className="text-right">${(payout.loanAmount || 0).toLocaleString()}</TableCell>
                                  <TableCell className="text-right">
                                    ${(payout.grossCheckReceived || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right text-green-600">
                                    +${(payout.processingFee || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right text-green-600">
                                    +${(payout.eAndO || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-green-600">
                                    ${(payout.companyRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">{payout.date}</TableCell>
                                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                    {payout.type === 'Brokered' ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setEditPayoutDialog({
                                          payout,
                                          newRecipientId: payout.loId
                                        })}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    ) : (
                                      <span className="text-muted-foreground">&mdash;</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={revenueGrouping !== 'lo' ? 10 : 9} className="p-4">
                                      <CalculationBreakdown payout={payout} />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* --- BANKED LOANS TABLE --- */}
                  {bankedPayouts.length > 0 && (
                    <div>
                      {brokeredPayouts.length > 0 && (
                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2 pt-2 border-t">Banked</div>
                      )}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <SortableHeader field="loanNumber">Loan #</SortableHeader>
                            {revenueGrouping !== 'lo' && <SortableHeader field="loName">LO</SortableHeader>}
                            <SortableHeader field="loanAmount" align="right">Loan Amount</SortableHeader>
                            <TableHead className="text-right">Company Revenue</TableHead>
                            <TableHead className="text-right">E&O</TableHead>
                            <TableHead className="text-right">Net Profit</TableHead>
                            <SortableHeader field="date">Purchase Date</SortableHeader>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bankedPayouts.map((payout) => {
                            const isExpanded = expandedRows.has(`rev-${payout.id}`);
                            const netProfit = computeBankedNetProfit(payout);
                            return (
                              <React.Fragment key={payout.id}>
                                <TableRow
                                  className={`cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'bg-muted/30' : ''}`}
                                  onClick={() => toggleRowExpansion(`rev-${payout.id}`)}
                                >
                                  <TableCell className="w-8 p-2">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div>
                                      <div>{payout.loanNumber}</div>
                                      {payout.borrowerName && (
                                        <div className="text-sm text-muted-foreground font-normal">{payout.borrowerName}</div>
                                      )}
                                    </div>
                                  </TableCell>
                                  {revenueGrouping !== 'lo' && <TableCell>{payout.loName}</TableCell>}
                                  <TableCell className="text-right">${(payout.loanAmount || 0).toLocaleString()}</TableCell>
                                  <TableCell className="text-right font-medium text-green-600">
                                    ${(payout.companyRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right text-green-600">
                                    +${(payout.eAndO || 0).toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-blue-600">
                                    ${netProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    <div>{payout.date}</div>
                                    {payout.purchaseDate && payout.fundDate !== payout.purchaseDate && (
                                      <div className="text-xs text-muted-foreground/60">Funded {payout.fundDate}</div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                    {payout.type === 'Brokered' ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => setEditPayoutDialog({
                                          payout,
                                          newRecipientId: payout.loId
                                        })}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    ) : (
                                      <span className="text-muted-foreground">&mdash;</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={revenueGrouping !== 'lo' ? 9 : 8} className="p-4">
                                      <CalculationBreakdown payout={payout} />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Grand Total */}
          {payouts.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2">
              {revenueStats.brokeredUnits > 0 && (
                <Card className="border-2 border-green-500/20 bg-green-50/50">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-green-600" />
                        <span className="font-medium">Brokered Revenue {revenuePeriodLabel}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-600">
                          ${revenueStats.brokeredRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {revenueStats.brokeredUnits} loan{revenueStats.brokeredUnits !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {revenueStats.bankedUnits > 0 && (
                <Card className="border-2 border-blue-500/20 bg-blue-50/50">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-blue-600" />
                        <span className="font-medium">Banked Net Profit {revenuePeriodLabel}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          ${revenueStats.bankedNetProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {revenueStats.bankedUnits} loan{revenueStats.bankedUnits !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Payments Tab Content */}
        <TabsContent value="payments" className="mt-6 space-y-6">
          {/* Payment Stats */}
          {(() => {
            // Enrich payouts with payment period info
            const enrichedPayouts = payouts.map(payout => {
              const periodInfo = getPaymentPeriod(payout.fundDate);
              return {
                ...payout,
                paymentPayDate: periodInfo.payDate,
                paymentPayPeriod: periodInfo.payPeriod,
                paymentPayMonth: periodInfo.payMonth
              };
            });

            // Filter by selected month and pay period
            const filteredPayouts = enrichedPayouts.filter(payout => {
              // Skip month filter if "all" is selected
              if (paymentsPayMonth && paymentsPayMonth !== 'all' && payout.paymentPayMonth !== paymentsPayMonth) {
return false;
}
              if (paymentsPayPeriod !== 'all' && payout.paymentPayPeriod !== paymentsPayPeriod) {
return false;
}
              return true;
            });

            const totalOwed = filteredPayouts.reduce((sum, p) => sum + (p.netPayout || 0), 0);
            const totalPaid = filteredPayouts.reduce((sum, p) => sum + (p.paymentAmount || 0), 0);
            const totalRecaptured = filteredPayouts.reduce((sum, p) => sum + (p.recapturedAmount || 0), 0);
            const totalBalance = computeBalance(totalOwed, totalPaid, totalRecaptured);

            // Group by LO
            const loBalancesList = computeLoBalances(filteredPayouts)
              .map(lo => ({ ...lo, payouts: filteredPayouts.filter(payout => payout.loId === lo.loId) }))
              .filter(lo => isPayoutOutstanding(lo.totalOwed, lo.totalPaid, lo.totalRecaptured) || lo.payouts.some(p => p.type === 'Volume Bonus' && isPayoutOutstanding(p.netPayout, p.paymentAmount, 0, p.type)))
              .sort((a, b) => b.balance - a.balance);

            return (
              <>
                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="payments-pay-month" className="text-sm font-medium whitespace-nowrap">Pay Month:</Label>
                    <Select value={paymentsPayMonth} onValueChange={setPaymentsPayMonth}>
                      <SelectTrigger id="payments-pay-month" className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Periods</SelectItem>
                        {monthOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium whitespace-nowrap">Pay Period:</Label>
                    <div className="flex gap-1">
                      <Button
                        variant={paymentsPayPeriod === 'all' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentsPayPeriod('all')}
                      >
                        All
                      </Button>
                      <Button
                        variant={paymentsPayPeriod === '15th' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentsPayPeriod('15th')}
                      >
                        15th
                      </Button>
                      <Button
                        variant={paymentsPayPeriod === 'EOM' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPaymentsPayPeriod('EOM')}
                      >
                        EOM
                      </Button>
                    </div>
                  </div>

                  <div className="ml-auto text-sm text-muted-foreground">
                    Showing {filteredPayouts.length} of {payouts.length} payouts
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Owed</CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        ${totalOwed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        ${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Outstanding Balance</CardTitle>
                      <Wallet className="h-4 w-4 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-orange-600">
                        ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      {totalRecaptured > 0 && <p className="text-xs text-muted-foreground">${totalRecaptured.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} recovered from commissions</p>}
                    </CardContent>
                  </Card>
                </div>

                {/* LO Balances */}
                {loBalancesList.map((lo) => {
                  const balance = computeBalance(lo.totalOwed || 0, lo.totalPaid || 0, lo.totalRecaptured);
                  const unpaidPayouts = lo.payouts.filter(p => isPayoutOutstanding(p.netPayout || 0, p.paymentAmount || 0, p.recapturedAmount, p.type));
                  const allLoSelected = unpaidPayouts.length > 0 && unpaidPayouts.every(p => selectedForPayment.has(p.id));
                  const someLoSelected = unpaidPayouts.some(p => selectedForPayment.has(p.id));
                  const selectedLoPayouts = unpaidPayouts.filter(p => selectedForPayment.has(p.id));
                  const selectedLoTotal = selectedLoPayouts.reduce((sum, p) => sum + computeBalance(p.netPayout, p.paymentAmount, p.recapturedAmount), 0);

                  return (
                    <Card key={lo.loId}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <User className="h-5 w-5" />
                              {lo.loName}
                            </CardTitle>
                            <PendingRecaptureNotice records={recaptureWarnings} recipientId={lo.loId} />
                            <CardDescription>
                              {unpaidPayouts.length} unpaid payout{unpaidPayouts.length !== 1 ? 's' : ''}
                              {selectedLoPayouts.length > 0 && (
                                <span className="text-primary ml-2">
                                  ({selectedLoPayouts.length} selected = ${selectedLoTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })})
                                </span>
                              )}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-4">
                            {selectedLoPayouts.length > 0 && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => setBulkPaymentDialog({
                                  payouts: selectedLoPayouts.map(p => ({
                                    ...p,
                                    enteredAmount: computeBalance(p.netPayout || 0, p.paymentAmount || 0, p.recapturedAmount).toFixed(2)
                                  })),
                                  checkNumber: '',
                                  paymentDate: new Date().toLocaleDateString('en-CA'),
                                  memo: '',
                                  idempotencyKey: crypto.randomUUID()
                                })}
                              >
                                Pay {selectedLoPayouts.length} Selected (${selectedLoTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })})
                              </Button>
                            )}
                            <div className="text-right">
                              <div className="text-2xl font-bold text-orange-600">
                                ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              <div className="text-sm text-muted-foreground">Balance Owed</div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10">
                                <Checkbox
                                  checked={allLoSelected}
                                  ref={(el) => {
                                    if (el) {
                                      (el as HTMLButtonElement & { indeterminate?: boolean }).indeterminate = someLoSelected && !allLoSelected;
                                    }
                                  }}
                                  onCheckedChange={(checked) => {
                                    setSelectedForPayment(prev => {
                                      const next = new Set(prev);
                                      unpaidPayouts.forEach(p => {
                                        if (checked) {
                                          next.add(p.id);
                                        } else {
                                          next.delete(p.id);
                                        }
                                      });
                                      return next;
                                    });
                                  }}
                                />
                              </TableHead>
                              <TableHead>Loan #</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Fund Date</TableHead>
                              <TableHead className="text-right">Net Owed</TableHead>
                              <TableHead className="text-right">Paid</TableHead>
                              <TableHead className="text-right">Balance</TableHead>
                              <TableHead>Latest Check</TableHead>
                              <TableHead>Latest Payment</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {unpaidPayouts.map((payout) => {
                              const payoutBalance = computeBalance(payout.netPayout || 0, payout.paymentAmount || 0, payout.recapturedAmount);
                              const isSelected = selectedForPayment.has(payout.id);
                              return (
                                <TableRow key={payout.id} className={isSelected ? 'bg-primary/5' : ''}>
                                  <TableCell>
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={(checked) => {
                                        setSelectedForPayment(prev => {
                                          const next = new Set(prev);
                                          if (checked) {
                                            next.add(payout.id);
                                          } else {
                                            next.delete(payout.id);
                                          }
                                          return next;
                                        });
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div>
                                      <div>{payout.loanNumber}</div>
                                      {payout.borrowerName && (
                                        <div className="text-sm text-muted-foreground">{payout.borrowerName}</div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div>
                                      <Badge variant="outline" className={
                                        payout.type === 'Brokered' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                        payout.type === 'Override' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                                        payout.type === 'Mentor' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                                        payout.type === 'Banked' ? 'bg-indigo-100 text-indigo-800 border-indigo-300' : ''
                                      }>
                                        {payout.type}
                                      </Badge>
                                      {payout.type === 'Brokered' && mentorByLoan.has(payout.loanNumber) && (
                                        <div className="text-xs text-purple-600 mt-1">
                                          Mentored by {mentorByLoan.get(payout.loanNumber)}
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>{payout.fundDate}</TableCell>
                                  <TableCell className="text-right">
                                    ${payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right text-green-600">
                                    {payout.paymentAmount > 0
                                      ? `$${payout.paymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : '-'
                                    }
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-orange-600">
                                    ${payoutBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    <PayoutRecaptureNotice records={recaptureRecords} payoutId={payout.id} />
                                  </TableCell>
                                  <TableCell>{payout.paymentCheckNumber || '-'}</TableCell>
                                  <TableCell>{payout.paymentDate || '-'}</TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setPaymentDialog({
                                          payout,
                                          checkNumber: '',
                                          paymentDate: new Date().toLocaleDateString('en-CA'),
                                          paymentAmount: computeDialogDefault(payout.netPayout, payout.paymentAmount || 0, payout.recapturedAmount, payout.type).toFixed(2),
                                          memo: '',
                                          idempotencyKey: crypto.randomUUID()
                                        });
                                      }}
                                    >
                                      {(payout.paymentAmount || 0) > 0 ? 'Add Payment' : 'Record Payment'}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  );
                })}

                {loBalancesList.length === 0 && (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-600" />
                      <p>All payments are up to date!</p>
                    </CardContent>
                  </Card>
                )}

                {/* Immutable payment history grouped by physical check. */}
                <Card>
                  <CardHeader>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          Payment History
                        </CardTitle>
                        <CardDescription>
                          One record per physical check. Voided checks remain visible and are excluded from paid totals.
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={visiblePaymentBatches.length === 0}
                        onClick={() => void downloadPayoutPaymentLedger(visiblePaymentBatches)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export Ledger
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          className="pl-9"
                          placeholder="Search check, payee, loan, or borrower"
                          value={paymentHistorySearch}
                          onChange={(event) => setPaymentHistorySearch(event.target.value)}
                        />
                      </div>
                      <Select
                        value={paymentHistoryStatus}
                        onValueChange={(value) => setPaymentHistoryStatus(value as PaymentBatchStatusFilter)}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All checks</SelectItem>
                          <SelectItem value="active">Active checks</SelectItem>
                          <SelectItem value="voided">Voided checks</SelectItem>
                          <SelectItem value="legacy">Legacy imports</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {paymentBatchesLoading ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">Loading payment history…</div>
                    ) : visiblePaymentBatches.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground">No payment checks match these filters.</div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-10" />
                            <TableHead className="cursor-pointer select-none" onClick={() => handlePaymentHistorySort('checkNumber')}>
                              <div className="flex items-center gap-1">
                                Check #
                                {paymentHistorySort === 'checkNumber'
                                  ? paymentHistorySortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                              </div>
                            </TableHead>
                            <TableHead className="cursor-pointer select-none" onClick={() => handlePaymentHistorySort('paymentDate')}>
                              <div className="flex items-center gap-1">
                                Payment Date
                                {paymentHistorySort === 'paymentDate'
                                  ? paymentHistorySortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                              </div>
                            </TableHead>
                            <TableHead className="cursor-pointer select-none" onClick={() => handlePaymentHistorySort('recipientName')}>
                              <div className="flex items-center gap-1">
                                Payee
                                {paymentHistorySort === 'recipientName'
                                  ? paymentHistorySortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                              </div>
                            </TableHead>
                            <TableHead>Allocations</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="cursor-pointer select-none text-right" onClick={() => handlePaymentHistorySort('totalAmount')}>
                              <div className="flex items-center justify-end gap-1">
                                Check Total
                                {paymentHistorySort === 'totalAmount'
                                  ? paymentHistorySortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                              </div>
                            </TableHead>
                            <TableHead className="w-32 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visiblePaymentBatches.map((batch) => {
                            const isExpanded = expandedPaymentBatches.has(batch.id);
                            const isVoided = Boolean(batch.voidedAt);
                            return (
                              <React.Fragment key={batch.id}>
                                <TableRow className={isVoided ? 'bg-muted/30 text-muted-foreground' : ''}>
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} check ${batch.checkNumber}`}
                                      aria-expanded={isExpanded}
                                      onClick={() => togglePaymentBatchExpansion(batch.id)}
                                    >
                                      <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                    </Button>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {batch.checkNumber}
                                    {!batch.historyComplete && (
                                      <Badge variant="outline" className="ml-2 text-xs">Legacy</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>{batch.paymentDate}</TableCell>
                                  <TableCell>{batch.recipientName}</TableCell>
                                  <TableCell>{batch.allocations.length} payout{batch.allocations.length !== 1 ? 's' : ''}</TableCell>
                                  <TableCell>
                                    {isVoided ? (
                                      <Badge variant="destructive">Voided</Badge>
                                    ) : (
                                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold tabular-nums">
                                    ${batch.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center justify-end gap-1">
                                      {batch.paymentEmailSentAt ? (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Badge variant="outline" className="bg-success/10 text-success border-success/30">
                                                <CheckCircle2 className="mr-1 h-3 w-3" />
                                                Emailed
                                              </Badge>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              Sent to {batch.paymentEmailRecipient || batch.recipientName} on {new Date(batch.paymentEmailSentAt).toLocaleString()}
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : !isVoided && batch.historyComplete ? (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                aria-label={`Email payment details for check ${batch.checkNumber}`}
                                                onClick={() => openPaymentEmailDialog(batch)}
                                              >
                                                <Mail className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Email payment details to {batch.recipientName}</TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : null}
                                      {!isVoided && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                aria-label={`Void check ${batch.checkNumber}`}
                                                onClick={() => setVoidPaymentDialog({ batch, reason: '' })}
                                              >
                                                <Ban className="h-4 w-4" />
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>Void entire check</TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={8} className="bg-muted/20 p-4">
                                      <div className="space-y-3">
                                        {!batch.historyComplete && (
                                          <p className="text-xs text-orange-700">
                                            Legacy import: the prior system may have overwritten older check details. Reconcile this entry to source checks before correcting it.
                                          </p>
                                        )}
                                        <div className="rounded-md border bg-background">
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>Loan #</TableHead>
                                                <TableHead>Borrower</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead className="text-right">Allocation</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {batch.allocations.map((allocation) => (
                                                <TableRow key={allocation.id}>
                                                  <TableCell className="font-mono text-xs">{allocation.loanNumber}</TableCell>
                                                  <TableCell>{allocation.borrowerName || '-'}</TableCell>
                                                  <TableCell className="capitalize">{allocation.payoutType}</TableCell>
                                                  <TableCell className="text-right font-medium tabular-nums">
                                                    ${allocation.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </div>
                                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                                          <span>Recorded by {batch.createdByName || 'Unknown'} on {new Date(batch.createdAt).toLocaleString()}</span>
                                          {batch.memo && <span>Memo: {batch.memo}</span>}
                                          {batch.paymentEmailSentAt && (
                                            <span className="text-success">
                                              Emailed to {batch.paymentEmailRecipient || batch.recipientName} by {batch.paymentEmailSentByName || 'Unknown'} on {new Date(batch.paymentEmailSentAt).toLocaleString()}
                                            </span>
                                          )}
                                          {isVoided && (
                                            <span className="text-destructive">
                                              Voided by {batch.voidedByName || 'Unknown'} on {batch.voidedAt ? new Date(batch.voidedAt).toLocaleString() : '-'}: {batch.voidReason}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </TabsContent>

        {/* Balances Tab */}
        <TabsContent value="balances" className="mt-6 space-y-6">
          {(() => {
            // Group all payouts by LO using shared utility (tested in paymentCalculations.test.ts)
            const loSummaries = computeLoBalances(payouts);

            // Also group raw PayoutData per LO for the expanded detail table
            const payoutsByLO: Record<string, PayoutData[]> = {};
            for (const payout of payouts) {
              if (!payoutsByLO[payout.loId]) {
payoutsByLO[payout.loId] = [];
}
              payoutsByLO[payout.loId].push(payout);
            }

            const balanceList = loSummaries.map(lo => ({
              ...lo,
              outstanding: isPayoutOutstanding(lo.totalOwed, lo.totalPaid, lo.totalRecaptured)
                || (payoutsByLO[lo.loId] || []).some(payout => payout.type === 'Volume Bonus'
                  && isPayoutOutstanding(payout.netPayout, payout.paymentAmount, 0, payout.type)),
              payouts: payoutsByLO[lo.loId] || []
            }));
            const grandTotalOwed = balanceList.reduce((s, lo) => s + lo.totalOwed, 0);
            const grandTotalPaid = balanceList.reduce((s, lo) => s + lo.totalPaid, 0);
            const grandTotalRecaptured = balanceList.reduce((sum, lo) => sum + lo.totalRecaptured, 0);
            const grandBalance = computeBalance(grandTotalOwed, grandTotalPaid, grandTotalRecaptured);

            // Filter state
            const filteredBalanceList = balanceList.filter(lo => {
              if (balanceSearch && !lo.loName.toLowerCase().includes(balanceSearch.toLowerCase())) {
return false;
}
              if (balanceFilter === 'outstanding' && !lo.outstanding) {
return false;
}
              if (balanceFilter === 'settled' && lo.outstanding) {
return false;
}
              return true;
            });

            const toggleBalanceLO = (loId: string) => {
              setExpandedBalanceLOs(prev => {
                const next = new Set(prev);
                if (next.has(loId)) {
next.delete(loId);
} else {
next.add(loId);
}
                return next;
              });
            };

            return (
              <>
                {/* Filter Controls */}
                <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium whitespace-nowrap">Search:</Label>
                    <Input
                      placeholder="Filter by LO name..."
                      value={balanceSearch}
                      onChange={(e) => setBalanceSearch(e.target.value)}
                      className="w-[200px]"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium whitespace-nowrap">Show:</Label>
                    <div className="flex gap-1">
                      <Button variant={balanceFilter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setBalanceFilter('all')}>All</Button>
                      <Button variant={balanceFilter === 'outstanding' ? 'default' : 'outline'} size="sm" onClick={() => setBalanceFilter('outstanding')}>Outstanding</Button>
                      <Button variant={balanceFilter === 'settled' ? 'default' : 'outline'} size="sm" onClick={() => setBalanceFilter('settled')}>Settled</Button>
                    </div>
                  </div>
                  <div className="ml-auto text-sm text-muted-foreground">
                    {filteredBalanceList.length} of {balanceList.length} loan officers
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Owed</CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        ${grandTotalOwed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <p className="text-xs text-muted-foreground">{payouts.length} total payouts</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">
                        ${grandTotalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <p className="text-xs text-muted-foreground">{balanceList.reduce((s, lo) => s + lo.paidCount, 0)} payouts settled</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Outstanding Balance</CardTitle>
                      <Wallet className="h-4 w-4 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                      <div className={`text-2xl font-bold ${isPayoutOutstanding(grandTotalOwed, grandTotalPaid, grandTotalRecaptured) ? 'text-orange-600' : 'text-green-600'}`}>
                        ${grandBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <p className="text-xs text-muted-foreground">{balanceList.reduce((s, lo) => s + lo.unpaidCount, 0)} payouts unpaid · ${grandTotalRecaptured.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} recaptured</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Per-LO Balance Table */}
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Loan Officer</TableHead>
                          <TableHead className="text-center">Loans</TableHead>
                          <TableHead className="text-right">Total Owed</TableHead>
                          <TableHead className="text-right">Total Paid</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBalanceList.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                              No loan officers match the current filter
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredBalanceList.map((lo) => {
                            const isExpanded = expandedBalanceLOs.has(lo.loId);
                            const sortedPayouts = [...lo.payouts].sort((a, b) =>
                              new Date(b.fundDate).getTime() - new Date(a.fundDate).getTime()
                            );

                            return (
                              <React.Fragment key={lo.loId}>
                                <TableRow
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => toggleBalanceLO(lo.loId)}
                                >
                                  <TableCell className="w-8">
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </TableCell>
                                  <TableCell className="font-medium">{lo.loName}<PendingRecaptureNotice records={recaptureWarnings} recipientId={lo.loId} /></TableCell>
                                  <TableCell className="text-center">{new Set(lo.payouts.map(p => p.loanNumber)).size}</TableCell>
                                  <TableCell className="text-right">
                                    ${lo.totalOwed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="text-right text-green-600">
                                    ${lo.totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className={`text-right font-semibold ${lo.outstanding ? 'text-orange-600' : 'text-green-600'}`}>
                                    ${lo.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    {lo.totalRecaptured > 0 && <p className="text-xs font-normal text-muted-foreground">${lo.totalRecaptured.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} recaptured</p>}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {!lo.outstanding ? (
                                      <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Settled</Badge>
                                    ) : (
                                      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Outstanding</Badge>
                                    )}
                                  </TableCell>
                                </TableRow>

                                {/* Expanded: individual payouts */}
                                {isExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={7} className="p-0 bg-muted/30">
                                      <div className="p-4">
                                        <RecaptureAccountSummary recipientId={lo.loId} />
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              <TableHead>Loan #</TableHead>
                                              <TableHead>Borrower</TableHead>
                                              <TableHead>Type</TableHead>
                                              <TableHead>Fund Date</TableHead>
                                              <TableHead className="text-right">Base Comp</TableHead>
                                              <TableHead className="text-right">Reimb.</TableHead>
                                              <TableHead className="text-right">E&O</TableHead>
                                              <TableHead className="text-right">Net Owed</TableHead>
                                              <TableHead className="text-right">Paid</TableHead>
                                              <TableHead>Latest Check</TableHead>
                                              <TableHead>Latest Payment</TableHead>
                                              <TableHead className="text-right">Balance</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {sortedPayouts.map((p) => {
                                              const pBalance = computeBalance(p.netPayout || 0, p.paymentAmount || 0, p.recapturedAmount);
                                              const reimbursements = p.loReimbursementsTotal || 0;
                                              const baseComp = computeBaseComp(p.loCommissionBase, p.grossPayout || 0, reimbursements);
                                              return (
                                                <TableRow key={p.id}>
                                                  <TableCell className="font-mono text-xs">{p.loanNumber}<PayoutRecaptureNotice records={recaptureRecords} payoutId={p.id} /></TableCell>
                                                  <TableCell>{p.borrowerName}</TableCell>
                                                  <TableCell>
                                                    <Badge variant="outline" className="text-xs">{p.type}</Badge>
                                                  </TableCell>
                                                  <TableCell>{p.fundDate ? new Date(`${p.fundDate  }T12:00:00`).toLocaleDateString() : '-'}</TableCell>
                                                  <TableCell className="text-right">
                                                    ${baseComp.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </TableCell>
                                                  <TableCell className={`text-right ${reimbursements > 0 ? 'text-blue-600 font-medium' : 'text-muted-foreground'}`}>
                                                    {reimbursements > 0 ? (() => {
                                                      // Show flagged non-standard loan revenue items (Credit Report, etc.)
                                                      // that make up the reimbursement total.
                                                      return (
                                                        <TooltipProvider>
                                                          <Tooltip>
                                                            <TooltipTrigger className="cursor-help underline decoration-dotted underline-offset-2">
                                                              ${reimbursements.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="max-w-xs">
                                                              <PayoutAccountingDetails reconId={p.bankerReconId} source={p.bankerRecon}
                                                                initial={p.bankerRecon?.detailsLoaded ? p.bankerRecon : undefined}>
                                                                {details => {
                                                                  const standardFees = ['processing_fee', 'underwriting_fee', 'interest_charged'];
                                                                  const reimbItems = details?.line_items.filter(li => li.category === 'loan_revenue' && li.reimbursable && !standardFees.includes(li.line_type)) || [];
                                                                  return <div className="space-y-1 text-xs">
                                                                    <p className="font-semibold">LO Reimbursements:</p>
                                                                    {reimbItems.length > 0 ? reimbItems.map(li => (
                                                                      <div key={li.id} className="flex justify-between gap-4">
                                                                        <span>{li.description}</span>
                                                                        <span className="font-mono">${getLoanRevenueReimbursementBasis(li).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                                      </div>
                                                                    )) : (
                                                                      <div className="flex justify-between gap-4">
                                                                        <span>Reimbursable fees</span>
                                                                        <span className="font-mono">${reimbursements.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                                                      </div>
                                                                    )}
                                                                  </div>;
                                                                }}
                                                              </PayoutAccountingDetails>
                                                            </TooltipContent>
                                                          </Tooltip>
                                                        </TooltipProvider>
                                                      );
                                                    })() : '-'}
                                                  </TableCell>
                                                  <TableCell className="text-right text-muted-foreground">
                                                    ${p.eAndO.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </TableCell>
                                                  <TableCell className="text-right font-medium">
                                                    ${(p.netPayout || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </TableCell>
                                                  <TableCell className="text-right text-green-600">
                                                    {p.paymentAmount > 0 ? `$${p.paymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                  </TableCell>
                                                  <TableCell>{p.paymentCheckNumber || '-'}</TableCell>
                                                  <TableCell>{p.paymentDate ? new Date(`${p.paymentDate  }T12:00:00`).toLocaleDateString() : '-'}</TableCell>
                                                  <TableCell className={`text-right font-medium ${isPayoutOutstanding(p.netPayout || 0, p.paymentAmount || 0, p.recapturedAmount, p.type) ? 'text-orange-600' : 'text-green-600'}`}>
                                                    ${pBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                  </TableCell>
                                                </TableRow>
                                              );
                                            })}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialog !== null} onOpenChange={(open) => !open && setEditDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Compensation</DialogTitle>
            <DialogDescription>
              Configure compensation settings for {editDialog?.userName}
            </DialogDescription>
          </DialogHeader>

          {editDialog && (
            <div className="space-y-6">
              {/* Compensation Amounts */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Compensation Amounts</h3>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Row 1, Column 1: Banked Compensation */}
                  <div className="space-y-2">
                    <Label htmlFor="banked_compensation">Banked Compensation (%)</Label>
                    <Select
                      value={editDialog.banked_compensation?.toFixed(2) || ''}
                      onValueChange={(value) => setEditDialog({
                        ...editDialog,
                        banked_compensation: value ? parseFloat(value) : null
                      })}
                    >
                      <SelectTrigger id="banked_compensation">
                        <SelectValue placeholder="Select percentage (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {BANKED_COMPENSATION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Range: 0.75% - 3.00%</p>
                  </div>

                  {/* Row 1, Column 2: Broker Commission Split */}
                  <div className="space-y-2">
                    <Label htmlFor="broker_commission_split">Broker Commission Split (%)</Label>
                    <Input
                      id="broker_commission_split"
                      type="number"
                      placeholder="e.g., 75"
                      min={0}
                      max={85}
                      step={1}
                      value={editDialog.broker_commission_split || ''}
                      onChange={(e) => setEditDialog({
                        ...editDialog,
                        broker_commission_split: e.target.value ? parseFloat(e.target.value) : null
                      })}
                    />
                    <p className="text-xs text-muted-foreground">Range: 0% - 85%</p>
                  </div>

                  {/* Row 2: Maximum Compensation (Full Width) */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="maximum_compensation">Maximum Compensation</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                      <Input
                        id="maximum_compensation"
                        type="text"
                        maxLength={9}
                        placeholder="50,000"
                        className="pl-8"
                        onChange={(e) => {
                          // Remove non-digit characters
                          const value = e.target.value.replace(/\D/g, '');

                          // Limit to 6 digits
                          const limitedValue = value.slice(0, 6);

                          // Format with thousand separator
                          const formattedValue = limitedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

                          // Update the input display
                          e.target.value = formattedValue;

                          // Set the actual numeric value in form
                          setEditDialog({
                            ...editDialog,
                            maximum_compensation: limitedValue ? parseInt(limitedValue, 10) : null
                          });
                        }}
                        value={
                          editDialog.maximum_compensation
                            ? editDialog.maximum_compensation.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                            : ''
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Maximum commission per deal ($10,000 - $999,999)</p>
                  </div>
                </div>
              </div>

              {/* Relationship Assignments */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Relationship Assignments</h3>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Override Recipient */}
                  <div className="space-y-2">
                    <Label htmlFor="override_recipient">Override Recipient</Label>
                    <Select
                      value={editDialog.override_recipient_id || 'none'}
                      onValueChange={(value) => setEditDialog({
                        ...editDialog,
                        override_recipient_id: value === 'none' ? null : value,
                        banked_override_bps: value === 'none' ? null : editDialog.banked_override_bps
                      })}
                    >
                      <SelectTrigger id="override_recipient">
                        <SelectValue placeholder="Select override recipient (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 opacity-50" />
                            None
                          </div>
                        </SelectItem>
                        {potentialMentors
                          .filter(u => u.id !== editDialog.userId)
                          .map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Override Amount - Always visible, disabled when no recipient */}
                  <div className="space-y-2">
                    <Label htmlFor="banked_override_bps">Override Amount (bps)</Label>
                    <Input
                      id="banked_override_bps"
                      type="number"
                      placeholder="e.g., 25"
                      min={0}
                      max={50}
                      step={1}
                      disabled={!editDialog.override_recipient_id}
                      value={editDialog.banked_override_bps || ''}
                      onChange={(e) => setEditDialog({
                        ...editDialog,
                        banked_override_bps: e.target.value ? parseInt(e.target.value) : null
                      })}
                    />
                    <p className="text-xs text-muted-foreground">Range: 0 - 50 bps</p>
                  </div>

                  {/* Mentor */}
                  <div className="space-y-2">
                    <Label htmlFor="mentor">Mentor</Label>
                    <Select
                      value={editDialog.mentor_id || 'none'}
                      onValueChange={(value) => setEditDialog({
                        ...editDialog,
                        mentor_id: value === 'none' ? null : value,
                        broker_mentor_commission_split: value === 'none' ? null : editDialog.broker_mentor_commission_split
                      })}
                    >
                      <SelectTrigger id="mentor">
                        <SelectValue placeholder="Select mentor (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 opacity-50" />
                            None
                          </div>
                        </SelectItem>
                        {potentialMentors
                          .filter(u => u.id !== editDialog.userId)
                          .map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Mentor Split - Always visible, disabled when no mentor */}
                  <div className="space-y-2">
                    <Label htmlFor="mentor_split">Mentor Commission Split (%)</Label>
                    <Input
                      id="mentor_split"
                      type="number"
                      placeholder="e.g., 25"
                      min={0}
                      max={50}
                      step={1}
                      disabled={!editDialog.mentor_id}
                      value={editDialog.broker_mentor_commission_split || ''}
                      onChange={(e) => setEditDialog({
                        ...editDialog,
                        broker_mentor_commission_split: e.target.value ? parseFloat(e.target.value) : null
                      })}
                    />
                    <p className="text-xs text-muted-foreground">Range: 0% - 50%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payout LO Assignment Dialog */}
      <Dialog open={editPayoutDialog !== null} onOpenChange={(open) => !open && setEditPayoutDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payout Assignment</DialogTitle>
            <DialogDescription>
              Change the loan officer for this payout
            </DialogDescription>
          </DialogHeader>

          {editPayoutDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Loan:</span>
                  <span className="font-medium">{editPayoutDialog.payout.loanNumber}</span>
                </div>
                {editPayoutDialog.payout.borrowerName && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Borrower:</span>
                    <span className="font-medium">{editPayoutDialog.payout.borrowerName}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current LO:</span>
                  <span className="font-medium">{editPayoutDialog.payout.loName}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-lo">New Loan Officer</Label>
                <Select
                  value={editPayoutDialog.newRecipientId}
                  onValueChange={(value) => setEditPayoutDialog({
                    ...editPayoutDialog,
                    newRecipientId: value
                  })}
                >
                  <SelectTrigger id="new-lo">
                    <SelectValue placeholder="Select loan officer" />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .filter(u => u.roles?.includes('loan_officer'))
                      .map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPayoutDialog(null)} disabled={savingPayout}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePayoutEdit}
              disabled={savingPayout || editPayoutDialog?.newRecipientId === editPayoutDialog?.payout.loId}
            >
              {savingPayout ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog
        open={paymentDialog !== null}
        onOpenChange={(open) => !open && !savingPayment && setPaymentDialog(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record payment for this payout
            </DialogDescription>
          </DialogHeader>

          {paymentDialog && (
            <div className="space-y-4">
              <PendingRecaptureNotice records={recaptureWarnings} recipientId={paymentDialog.payout.loId} />
              {paymentDialog.payout.type === 'Volume Bonus' && <p className="text-sm font-medium">{paymentDialog.payout.splits || 'Volume Bonus'}</p>}
              <div className="p-3 bg-muted rounded-lg space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Loan:</span>
                  <span className="font-medium">{paymentDialog.payout.loanNumber}</span>
                </div>
                {paymentDialog.payout.borrowerName && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Borrower:</span>
                    <span className="font-medium">{paymentDialog.payout.borrowerName}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">LO:</span>
                  <span className="font-medium">{paymentDialog.payout.loName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Owed:</span>
                  <span className="font-medium">
                    ${paymentDialog.payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {((paymentDialog.payout.paymentAmount || 0) > 0 || (paymentDialog.payout.recapturedAmount || 0) > 0) && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Already Paid:</span>
                      <span className="font-medium text-green-600">
                        ${paymentDialog.payout.paymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Recaptured:</span>
                      <span>{fmtCurrency(paymentDialog.payout.recapturedAmount || 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t pt-1">
                      <span className="text-muted-foreground font-medium">Remaining Balance:</span>
                      <span className="font-semibold text-orange-600">
                        ${computeBalance(paymentDialog.payout.netPayout, paymentDialog.payout.paymentAmount, paymentDialog.payout.recapturedAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}
                {(paymentDialog.payout.paymentAmount || 0) === 0 && (paymentDialog.payout.recapturedAmount || 0) === 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Balance:</span>
                    <span className="font-medium text-orange-600">
                      ${paymentDialog.payout.netPayout.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-amount">Payment Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="payment-amount"
                    type="number"
                    step="0.01"
                    className="pl-7"
                    value={paymentDialog.paymentAmount}
                    onChange={(e) => setPaymentDialog({
                      ...paymentDialog,
                      paymentAmount: e.target.value
                    })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="check-number">Check #</Label>
                <Input
                  id="check-number"
                  placeholder="e.g., 1234"
                  value={paymentDialog.checkNumber}
                  onChange={(e) => setPaymentDialog({
                    ...paymentDialog,
                    checkNumber: e.target.value
                  })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-date">Payment Date</Label>
                <Input
                  id="payment-date"
                  type="date"
                  value={paymentDialog.paymentDate}
                  onChange={(e) => setPaymentDialog({
                    ...paymentDialog,
                    paymentDate: e.target.value
                  })}
                  />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment-memo">Memo (optional)</Label>
                <Input
                  id="payment-memo"
                  placeholder="Correction or payroll note"
                  value={paymentDialog.memo}
                  onChange={(e) => setPaymentDialog({
                    ...paymentDialog,
                    memo: e.target.value
                  })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(null)} disabled={savingPayment}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePayment}
              disabled={
                savingPayment
                || !paymentDialog?.paymentAmount
                || !paymentDialog.checkNumber.trim()
                || !paymentDialog.paymentDate
                || !validatePaymentAmount(paymentDialog.paymentAmount).valid
              }
            >
              {savingPayment ? 'Saving...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Payment Dialog */}
      <Dialog
        open={bulkPaymentDialog !== null}
        onOpenChange={(open) => !open && !savingPayment && setBulkPaymentDialog(null)}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Record Bulk Payment</DialogTitle>
            <DialogDescription>
              Record payment for {bulkPaymentDialog?.payouts.length || 0} selected payouts with one check
            </DialogDescription>
          </DialogHeader>

          {bulkPaymentDialog && (
            <div className="space-y-4">
              <PendingRecaptureNotice records={recaptureWarnings} recipientId={bulkPaymentDialog.payouts[0]?.loId || ''} />
              <div className="p-3 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm font-medium">
                  <span>Selected Payouts:</span>
                  <span>{bulkPaymentDialog.payouts.length}</span>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2 border-t pt-2">
                  {bulkPaymentDialog.payouts.map((p, idx) => {
                    const balance = computeBalance(p.netPayout || 0, p.paymentAmount || 0, p.recapturedAmount);
                    return (
                      <div key={p.id} className="flex items-center gap-2 text-sm">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-xs truncate">{p.loanNumber}</div>
                          <div className="text-xs text-muted-foreground">{p.type === 'Volume Bonus' ? p.splits || p.type : p.type}</div>
                          {((p.paymentAmount || 0) > 0 || (p.recapturedAmount || 0) > 0) && (
                            <div className="text-xs text-muted-foreground">
                              Owed: ${(p.netPayout || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · Paid: ${(p.paymentAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} · Bal: ${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              {' '}· Recaptured: {fmtCurrency(p.recapturedAmount || 0)}
                            </div>
                          )}
                          {(p.paymentAmount || 0) === 0 && (p.recapturedAmount || 0) === 0 && (
                            <div className="text-xs text-muted-foreground">
                              Owed: ${(p.netPayout || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>
                        <div className="relative w-28 flex-shrink-0">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            className="pl-5 h-8 text-sm"
                            value={p.enteredAmount}
                            onChange={(e) => {
                              const updated = [...bulkPaymentDialog.payouts];
                              updated[idx] = { ...updated[idx], enteredAmount: e.target.value };
                              setBulkPaymentDialog({ ...bulkPaymentDialog, payouts: updated });
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-sm font-medium border-t pt-2">
                  <span>Total This Payment:</span>
                  <span className="text-orange-600">
                    ${bulkPaymentDialog.payouts.reduce((sum, p) => sum + (parseFloat(p.enteredAmount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-check-number">Check #</Label>
                <Input
                  id="bulk-check-number"
                  placeholder="e.g., 1234"
                  value={bulkPaymentDialog.checkNumber}
                  onChange={(e) => setBulkPaymentDialog({
                    ...bulkPaymentDialog,
                    checkNumber: e.target.value
                  })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-payment-date">Payment Date</Label>
                <Input
                  id="bulk-payment-date"
                  type="date"
                  value={bulkPaymentDialog.paymentDate}
                  onChange={(e) => setBulkPaymentDialog({
                    ...bulkPaymentDialog,
                    paymentDate: e.target.value
                  })}
                  />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bulk-payment-memo">Memo (optional)</Label>
                <Input
                  id="bulk-payment-memo"
                  placeholder="Correction or payroll note"
                  value={bulkPaymentDialog.memo}
                  onChange={(e) => setBulkPaymentDialog({
                    ...bulkPaymentDialog,
                    memo: e.target.value
                  })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPaymentDialog(null)} disabled={savingPayment}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveBulkPayment}
              disabled={
                savingPayment
                || !bulkPaymentDialog?.checkNumber.trim()
                || !bulkPaymentDialog.paymentDate
                || hasInvalidBulkPaymentAmounts(bulkPaymentDialog?.payouts.map(p => p.enteredAmount) || [])
              }
            >
              {savingPayment ? 'Saving...' : `Record Payment for ${bulkPaymentDialog?.payouts.length || 0} Payouts`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Optional LO payment confirmation shown only after the check is saved. */}
      <Dialog
        open={paymentEmailDialog !== null}
        onOpenChange={(open) => {
          if (!open && !sendingPaymentEmail) {
            setPaymentEmailDialog(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Email payment details
            </DialogTitle>
            <DialogDescription>
              Optional—review the saved check below, then choose whether to notify the loan officer.
            </DialogDescription>
          </DialogHeader>

          {paymentEmailDialog && (
            <div className="space-y-4">
              {paymentEmailDialog.paymentEmailSentAt && (
                <div
                  className="flex items-start gap-3 rounded-md border border-success/30 bg-success/10 p-4 text-success"
                  role="status"
                  aria-live="polite"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Payment email sent</p>
                    <p className="text-sm">
                      Sent to {paymentEmailDialog.paymentEmailRecipient || paymentEmailDialog.recipientName} on {new Date(paymentEmailDialog.paymentEmailSentAt).toLocaleString()} and recorded in Company Notifications.
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Recipient</p>
                    <p className="font-semibold">{paymentEmailDialog.recipientName}</p>
                    <p className="text-xs text-muted-foreground">Sent to the email on the loan officer&apos;s active Lendwise account</p>
                  </div>
                  <Badge variant="outline" className="w-fit">Check #{paymentEmailDialog.checkNumber}</Badge>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-xs text-muted-foreground">Payment date</p>
                    <p className="font-semibold">{parseAsLocalDate(paymentEmailDialog.paymentDate).toLocaleDateString()}</p>
                  </div>
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-xs text-muted-foreground">This check</p>
                    <p className="font-semibold">{fmtCurrency(paymentEmailDialog.totalAmount)}</p>
                  </div>
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-xs text-muted-foreground">Remaining balance</p>
                    <p className={paymentEmailDialog.remainingBalance > paymentEmailTolerance ? 'font-semibold text-warning' : 'font-semibold text-success'}>
                      {paymentEmailDialog.remainingBalance < 0 ? `${fmtCurrency(paymentEmailDialog.remainingBalance)} credit` : paymentEmailDialog.remainingBalance > paymentEmailTolerance ? fmtCurrency(paymentEmailDialog.remainingBalance) : 'Settled'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Loan</TableHead>
                      <TableHead>Borrower</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Owed</TableHead>
                      <TableHead className="text-right">This Check</TableHead>
                      <TableHead className="text-right">Total Paid</TableHead>
                      <TableHead className="text-right">Recaptured</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paymentEmailDialog.allocations.map((allocation) => (
                      <TableRow key={allocation.payoutId}>
                        <TableCell className="font-mono text-xs">{allocation.loanNumber}</TableCell>
                        <TableCell>{allocation.borrowerName || '-'}</TableCell>
                        <TableCell className="capitalize">{allocation.payoutType}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCurrency(allocation.amountOwed)}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{fmtCurrency(allocation.thisPayment)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCurrency(allocation.totalPaid)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtCurrency(allocation.recapturedAmount)}</TableCell>
                        <TableCell className={`text-right font-semibold tabular-nums ${allocation.remainingBalance > (allocation.isVolumeBonus ? 0 : 0.01) ? 'text-warning' : 'text-success'}`}>
                          {allocation.remainingBalance < 0 ? `${fmtCurrency(allocation.remainingBalance)} credit` : fmtCurrency(allocation.remainingBalance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <p className="text-xs text-muted-foreground">
                The email is generated from the saved payment ledger. Amounts and recipients cannot be edited here.
              </p>
            </div>
          )}

          <DialogFooter>
            {paymentEmailDialog?.paymentEmailSentAt ? (
              <Button onClick={() => setPaymentEmailDialog(null)}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setPaymentEmailDialog(null)} disabled={sendingPaymentEmail}>
                  Not now
                </Button>
                <Button onClick={handleSendPaymentEmail} disabled={sendingPaymentEmail || !paymentEmailDialog}>
                  {sendingPaymentEmail ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  {sendingPaymentEmail ? 'Sending…' : `Send to ${paymentEmailDialog?.recipientName || 'loan officer'}`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Whole-check void dialog */}
      <Dialog open={voidPaymentDialog !== null} onOpenChange={(open) => !open && setVoidPaymentDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Void Payment Check</DialogTitle>
            <DialogDescription>
              This excludes the entire check from paid totals. The check and every allocation remain permanently visible for audit.
            </DialogDescription>
          </DialogHeader>

          {voidPaymentDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Check #:</span>
                  <span className="font-medium">{voidPaymentDialog.batch.checkNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Payment Date:</span>
                  <span className="font-medium">{voidPaymentDialog.batch.paymentDate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Payee:</span>
                  <span className="font-medium">{voidPaymentDialog.batch.recipientName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Allocations:</span>
                  <span className="font-medium">{voidPaymentDialog.batch.allocations.length}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-1">
                  <span className="text-muted-foreground font-medium">Check Total:</span>
                  <span className="font-semibold">
                    ${voidPaymentDialog.batch.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              <div className="max-h-48 overflow-y-auto space-y-1">
                {voidPaymentDialog.batch.allocations.map((allocation) => (
                  <div key={allocation.id} className="flex items-center justify-between text-sm px-2 py-1 rounded hover:bg-muted/50">
                    <div className="min-w-0">
                      <span className="font-mono text-xs">{allocation.loanNumber}</span>
                      {allocation.borrowerName && <span className="text-muted-foreground ml-2">{allocation.borrowerName}</span>}
                    </div>
                    <span className="font-medium flex-shrink-0 ml-2">
                      ${allocation.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>

              {voidPaymentDialog.batch.paymentEmailSentAt && (
                <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning-foreground">
                  The loan officer was emailed about this check on {new Date(voidPaymentDialog.batch.paymentEmailSentAt).toLocaleString()}. Voiding the check does not recall that email; send the corrected replacement after re-entering it.
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="void-reason">Reason</Label>
                <Textarea
                  id="void-reason"
                  placeholder="Wrong amount, wrong check number, duplicate entry"
                  value={voidPaymentDialog.reason}
                  onChange={(e) => setVoidPaymentDialog({
                    ...voidPaymentDialog,
                    reason: e.target.value
                  })}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidPaymentDialog(null)} disabled={savingPayment}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleVoidPayment}
              disabled={savingPayment || !voidPaymentDialog || !voidPaymentDialog.reason.trim()}
            >
              {savingPayment ? 'Voiding...' : 'Void Entire Check'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compensation Drawer (Phase 2) */}
      <CompensationDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        user={selectedUser}
        potentialMentors={potentialMentors}
        onSave={handleDrawerSave}
      />
    </div>
  );
}
